import React, { useState, useMemo, useContext, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FixedSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";

import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import BoltIcon from "@mui/icons-material/Bolt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewListIcon from "@mui/icons-material/ViewList";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import { useSelector } from "react-redux";

import { useGetAllGroupsUnpagedQuery } from "../../redux/admin/groupApi";
import { useLazyGetCompactFleetDevicesQuery } from "../../redux/admin/deviceApi";
import { useUpdateAlarmRemarkMutation } from "../../redux/admin/alarmApi";
import { SocketContext } from "../../socket/SocketContext";
import { getResolvedLiveSnapshot } from "../../utils/vehicleStatus";
import { resolveUiDeviceStatus } from "../../utils/uiDeviceStatus";
import { resolveTrackingState } from "../../utils/trackingStateEngine";
import { useQuickModal } from "../../hooks/QuickModalContext";
import { useStatus } from "../../popup/NotificationContext";
import api from "../../redux/apis/api";
import { readDeviceViewCache, writeDeviceViewCache } from "../../utils/deviceViewCache";
import { beginProfile } from "../../utils/renderProfiler";
import { useDevRenderInstrumentation } from "../../utils/devInstrumentation";

// ─── Constants ───────────────────────────────────────────────────────────────

const DRAWER_WIDTH = "clamp(400px, 28vw, 430px)";
const finishDeviceSidebarProfile = beginProfile("DeviceSidebar", { stage: "component" });

const MOBILE_HEADER_OFFSET = 72;
const SIDEBAR_OBJECTS_PAGE_SIZE = 50;
const DEVICE_SIDEBAR_PAGE_CACHE_SCOPE = "device-sidebar-objects-page-v2";

const STATUS_ORDER = ["all", "running", "idle", "stopped", "inactive", "expired", "no_data"];
const normalizeSidebarStatus = (value) => {
  const allowed = new Set(["all", "running", "idle", "stopped", "inactive", "offline", "no_data", "nodata", "expired"]);
  const normalized = String(value || "all")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
  const aliases = {
    nodata: "no_data",
    no_reporting: "inactive",
    noreporting: "inactive",
    offline: "inactive",
  };
  const status = aliases[normalized] || normalized;
  return allowed.has(status) ? status : "all";
};

const getMapStatusFromUrl = () => {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("status") || "";
};
const STATUS_PILL_STYLE = {
  all:      { bg: "#d91c8e", color: "#fff", label: "All" },
  running:  { bg: "#16a34a", color: "#fff", label: "Running" },
  idle:     { bg: "#f59e0b", color: "#fff", label: "Idling" },
  stopped:  { bg: "#dc2626", color: "#fff", label: "Stopped" },
  inactive: { bg: "#2563eb", color: "#fff", label: "Not rep." },
  expired:  { bg: "#7c3aed", color: "#fff", label: "Expired" },
  no_data:  { bg: "#6b7280", color: "#fff", label: "No Data" },
};

const TABS = ["Objects", "Notifications", "History", "Geofence", "Landmark"];
const LANDMARK_CATEGORY_OPTIONS = [
  "provider",
  "customer",
  "garage",
  "emergency",
  "others",
  "gas_station",
  "residence",
  "city",
  "work",
  "restaurant",
  "shopping",
  "bank",
  "school",
  "super_market",
  "gym",
  "home",
  "grocery_store",
];
const SHARE_PRESETS = [
  { value: "1", label: "1 Hour", hours: 1 },
  { value: "8", label: "8 Hour", hours: 8 },
  { value: "24", label: "1 Day", hours: 24 },
  { value: "168", label: "7 Day", hours: 168 },
  { value: "custom", label: "Custom", hours: null },
];

const CLOUDINARY_BASE_URL = "https://res.cloudinary.com/dfob90lhg/image/upload";
const STATUS_TO_ICON_COLOR = {
  running: "green", idle: "yellow", stopped: "red",
  inactive: "blue", expired: "purple", no_data: "black", nodata: "black",
};
const DEVICE_TYPE_ALIASES = {
  mountainjeep:"car",jeep:"car",taxi:"car",taxi1:"car",supercar:"car",
  cargoship:"car",exporter:"car",custom:"car",cycle:"bike",motorcycle:"bike",
  scootor:"scooty",scooter:"scooty",rickshaw:"auto",autorickshaw:"auto",
  lorry:"truck",trailer:"truck",schoolbus:"schoolbus",school_bus:"schoolbus",
  deliveryvan:"van",delivery_van:"van",dumper:"dumpher",tipper:"dumpher",
  earthmover:"bulldozer",excavator:"jcb",police:"policecar",tracktor:"tractor",
};
const CLOUDINARY_ICON_FILES = {
  ambulance:{black:"Ambulance_black.png",blue:"Ambulance_Blue.png",green:"Ambulance_Green.png",purple:"Ambulance_purple.png",red:"ambulance_red.png",yellow:"Ambulance_Yellow.png"},
  animal:{black:"animal_black.png",blue:"animal_blue.png",green:"animal_green.png",purple:"animal_purple.png",red:"animal_red.png",yellow:"animal_yellow.png"},
  auto:{black:"Auto_black.png",blue:"Auto_blue.png",green:"Auto_green.png",purple:"Auto_purple.png",red:"Auto_Red.png",yellow:"Auto_Yellow.png"},
  bike:{black:"Bike_black.png",blue:"Bike_blue.png",green:"Bike_green.png",purple:"bike_purple.png",red:"Bike_red.png",yellow:"Bike_yellow.png"},
  boat:{black:"boat_black.png",blue:"boat_blue.png",green:"boat_green.png",purple:"boat_purple.png",red:"boat_red.png",yellow:"boat_yellow.png"},
  bulldozer:{black:"bulldozer_black.png",blue:"bulldozer_blue.png",green:"bulldozer_green.png",purple:"bulldozer_purple.png",red:"bulldozer_red.png",yellow:"bulldozer_yellow.png"},
  bus:{black:"bus_black.png",blue:"bus_blue.png",green:"bus_green.png",purple:"bus_purple.png",red:"bus_red.png",yellow:"bus_yellow.png"},
  car:{black:"Car_black.png",blue:"Car_blue.png",green:"Car_green.png",purple:"car_purple.png",red:"Car_Red.png",yellow:"Car_yellow.png"},
  cat:{black:"cat_black.png",blue:"cat_blue.png",green:"cat_green.png",purple:"cat_purple.png",red:"cat_red.png",yellow:"cat_yellow.png"},
  crane:{black:"crane_black.png",blue:"crane_blue.png",green:"crane_green.png",purple:"crane_purple.png",red:"crane_red.png",yellow:"crane_yellow.png"},
  dog:{black:"dog_black.png",blue:"dog_blue.png",green:"dog_green.png",purple:"dog_purple.png",red:"dog_red.png",yellow:"dog_yellow.png"},
  dumpher:{black:"dumpher_black.png",blue:"dumpher_blue.png",green:"dumpher_green.png",purple:"dumpher_purple.png",red:"dumpher_red.png",yellow:"dumpher_yellow.png"},
  female:{black:"female_black.png",blue:"female_blue.png",green:"female_green.png",purple:"female_purple.png",red:"female_red.png",yellow:"female_yellow.png"},
  femalechild:{black:"femalechild_black.png",blue:"femalechild_blue.png",green:"femalechild_green.png",purple:"femalechild_purple.png",red:"femalechild_red.png",yellow:"femalechild_yellow.png"},
  jcb:{black:"jcb_black.png",blue:"jcb_blue.png",green:"jcb_green.png",purple:"Jcb_purple.png",red:"jcb_red.png",yellow:"jcb_yellow.png"},
  luggage:{black:"luggage_black.png",blue:"luggage_blue.png",green:"luggage_green.png",purple:"luggage_purple.png",red:"luggage_red.png",yellow:"luggage_yellow.png"},
  machine:{black:"machine_black.png",blue:"machine_blue.png",green:"machine_green.png",purple:"machine_purple.png",red:"machine_red.png",yellow:"machine_yellow.png"},
  male:{black:"male_black.png",blue:"male_blue.png",green:"male_green.png",purple:"male_purple.png",red:"male_red.png",yellow:"male_yellow.png"},
  malechild:{black:"malechild_black.png",blue:"malechild_blue.png",green:"malechild_green.png",purple:"malechild_purple.png",red:"malechild_red.png",yellow:"malechild_yellow.png"},
  mixer:{black:"mixer_black.png",blue:"mixer_blue.png",green:"mixer_green.png",purple:"mixer_purple.png",red:"mixer_red.png",yellow:"mixer_yellow.png"},
  pickup:{black:"pickup_black.png",blue:"pickup_blue.png",green:"pickup_green.png",purple:"pickup_purple.png",red:"pickup_red.png",yellow:"pickup_yellow.png"},
  policebike:{black:"policebike_black.png",blue:"policebike_blue.png",green:"policebike_green.png",purple:"policebike_purple.png",red:"policebike_red.png",yellow:"policebike_yellow.png"},
  policebus:{black:"policebus_black.png",blue:"policebus_blue.png",green:"policebus_green.png",purple:"policebus_purple.png",red:"policebus_red.png",yellow:"policebus_yellow.png"},
  policecar:{black:"policecar_black.png",blue:"policecar_blue.png",green:"policecar_green.png",purple:"policecar_purple.png",red:"policecar_red.png",yellow:"policecar_yellow.png"},
  roadroller:{black:"roadroller_black.png",blue:"roadroller_blue.png",green:"roadroller_green.png",purple:"roadroller_purple.png",red:"roadroller_red.png",yellow:"roadroller_yellow.png"},
  schoolbus:{black:"schoolbus_black.png",blue:"schoolbus_blue.png",green:"schoolbus_green.png",purple:"schoolbus_purple.png",red:"schoolbus_red.png",yellow:"schoolbus_yellow.png"},
  scooty:{black:"scooty_black.png",blue:"scooty_blue.png",green:"scooty_green.png",purple:"Scooty_purple.png",red:"scooty_red.png",yellow:"scooty_yellow.png"},
  suv:{black:"suv_black.png",blue:"suv_blue.png",green:"suv_green.png",purple:"suv_purple.png",red:"suv_red.png",yellow:"suv_yellow.png"},
  tanker:{black:"tanker_black.png",blue:"tanker_blue.png",green:"tanker_green.png",purple:"tanker_purple.png",red:"tanker_red.png",yellow:"tanker_yellow.png"},
  tractor:{black:"tractor_black.png",blue:"tractor_blue.png",green:"tractor_green.png",purple:"tractor_purple.png",red:"tractor_red.png",yellow:"tractor_yellow.png"},
  truck:{black:"Truck_black.png",blue:"Truck_blue.png",green:"Truck_green.png",purple:"truck_purple.png",red:"truck_red.png",yellow:"Truck_yellow.png"},
  van:{black:"van_black.png",blue:"van_blue.png",green:"van_green.png",purple:"van_purple.png",red:"van_red.png",yellow:"van_yellow.png"},
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const safeLower = (v) => String(v || "").toLowerCase();

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const asNum = Number(trimmed);
      if (Number.isFinite(asNum)) return asNum < 1e12 ? asNum * 1000 : asNum;
    }
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseAccBool = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const l = value.trim().toLowerCase();
    if (["1","true","on","yes","acc_on","ignition_on","engine_on","engine_start","started"].includes(l)) return true;
    if (["0","false","off","no","acc_off","ignition_off","engine_off","engine_stop","stopped"].includes(l)) return false;
  }
  return null;
};

const parsePowerBool = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const l = value.trim().toLowerCase();
    if ([
      "1", "true", "on", "yes", "power_on", "external_power_on", "charging", "connected",
    ].includes(l)) return true;
    if ([
      "0", "false", "off", "no", "power_off", "power_cut", "external_power_cut", "external_power_off", "disconnected",
    ].includes(l)) return false;
  }
  return null;
};

const getBackendSidebarStatus = (device, liveInput = {}) => {
  return resolveUiDeviceStatus(device, [liveInput]).status;
};

const getSidebarLiveRenderSignature = (liveInput = {}, resolvedStatus = "no_data") => {
  const packetTs = toTimestampMs(
    liveInput?.packetReceivedAt ??
    liveInput?.lastSocketUpdate ??
    liveInput?.packetTime ??
    liveInput?.serverReceivedAt ??
    liveInput?.receivedAt ??
    liveInput?.timestamp ??
    liveInput?.serverTime ??
    liveInput?.lastDataReceivedAt ??
    liveInput?.lastUpdate
  );
  return [
    resolvedStatus,
    packetTs,
    liveInput?.latitude ?? liveInput?.lat ?? "",
    liveInput?.longitude ?? liveInput?.lng ?? "",
    liveInput?.speed ?? "",
    liveInput?.accStatus ?? liveInput?.acc ?? liveInput?.ignition ?? "",
    liveInput?.power ?? liveInput?.externalPower ?? liveInput?.mainPower ?? liveInput?.powerStatus ?? "",
  ].join("|");
};

const formatDate = (ts) => {
  if (!ts) return "";
  const d = new Date(toTimestampMs(ts));
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  });
};

const toYMD = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDateTimeFromParts = (date, hour, minute, meridiem) => {
  if (!date) return null;
  const h12 = Number(hour || 12);
  const mins = Number(minute || 0);
  let h24 = h12 % 12;
  if (String(meridiem || "AM").toUpperCase() === "PM") h24 += 12;
  const result = new Date(`${date}T00:00:00`);
  result.setHours(h24, mins, 0, 0);
  return result;
};

const getHistoryMinuteBucket = (minute, mode = "start") => {
  const supportedMinutes = [0, 15, 30, 45, 59];
  const numericMinute = Number(minute);
  if (!Number.isFinite(numericMinute)) return mode === "end" ? "59" : "00";
  if (mode === "end") {
    const bucket = supportedMinutes.find((value) => numericMinute <= value);
    return String(bucket ?? 59).padStart(2, "0");
  }
  let bucket = 0;
  supportedMinutes.forEach((value) => {
    if (numericMinute >= value) bucket = value;
  });
  return String(bucket).padStart(2, "0");
};

const getHistoryDateTimeParts = (value, mode = "start") => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const hours24 = date.getHours();
  const hour12 = hours24 % 12 || 12;
  return {
    date: toYMD(date),
    hour: String(hour12).padStart(2, "0"),
    minute: getHistoryMinuteBucket(date.getMinutes(), mode),
    meridiem: hours24 >= 12 ? "PM" : "AM",
  };
};

const formatDurationMins = (value) => {
  const totalMins = Math.max(0, Math.round(Number(value || 0)));
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")} min`;
};

const formatHistoryDistance = (value) => {
  const km = Number(value || 0);
  return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(2)} Kms`;
};

const extractHistoryAddress = (item, type) => {
  const candidates = type === "start"
    ? [
        item?.startAddress,
        item?.fromAddress,
        item?.originAddress,
        item?.pickupAddress,
        item?.startLocationAddress,
        item?.startLocationName,
        item?.startPlace,
        item?.startAddressText,
      ]
    : [
        item?.endAddress,
        item?.toAddress,
        item?.destinationAddress,
        item?.dropAddress,
        item?.endLocationAddress,
        item?.endLocationName,
        item?.endPlace,
        item?.endAddressText,
      ];
  return candidates.find((entry) => String(entry || "").trim()) || "NA";
};

const extractHistoryCoords = (item, type) => {
  const latCandidates = type === "start"
    ? [
        item?.startLat,
        item?.startLatitude,
        item?.fromLat,
        item?.fromLatitude,
        item?.startLocation?.lat,
        item?.startLocation?.latitude,
        item?.pickupLocation?.lat,
        item?.pickupLocation?.latitude,
      ]
    : [
        item?.endLat,
        item?.endLatitude,
        item?.toLat,
        item?.toLatitude,
        item?.endLocation?.lat,
        item?.endLocation?.latitude,
        item?.dropLocation?.lat,
        item?.dropLocation?.latitude,
      ];

  const lngCandidates = type === "start"
    ? [
        item?.startLng,
        item?.startLon,
        item?.startLongitude,
        item?.fromLng,
        item?.fromLon,
        item?.fromLongitude,
        item?.startLocation?.lng,
        item?.startLocation?.lon,
        item?.startLocation?.longitude,
        item?.pickupLocation?.lng,
        item?.pickupLocation?.longitude,
      ]
    : [
        item?.endLng,
        item?.endLon,
        item?.endLongitude,
        item?.toLng,
        item?.toLon,
        item?.toLongitude,
        item?.endLocation?.lng,
        item?.endLocation?.lon,
        item?.endLocation?.longitude,
        item?.dropLocation?.lng,
        item?.dropLocation?.longitude,
      ];

  const lat = latCandidates.map(Number).find((value) => Number.isFinite(value) && value !== 0);
  const lng = lngCandidates.map(Number).find((value) => Number.isFinite(value) && value !== 0);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const formatHistoryDateLabel = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const historyPointDistanceKm = (from, to) => {
  const lat1 = Number(from?.lat);
  const lng1 = Number(from?.lng);
  const lat2 = Number(to?.lat);
  const lng2 = Number(to?.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return 0;
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 6371 * c;
};

const getPlaybackPointAddress = (point) =>
  point?.resolvedAddress ||
  point?.geocodedAddress ||
  point?.address ||
  point?.locationAddress ||
  point?.currentAddress ||
  point?.displayAddress ||
  "NA";

const getPlaybackPointCoords = (point) => {
  const lat = Number(point?.lat ?? point?.latitude);
  const lng = Number(point?.lng ?? point?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const getPlaybackPointMode = (point) => {
  const explicit = safeLower(point?.vehicleStatus || point?.status).trim();
  const speed = Number(point?.speed || 0);
  const acc = parseAccBool(point?.accStatus ?? point?.acc ?? point?.ignition);
  if (explicit.includes("run") || explicit.includes("move")) return "ride";
  if (speed > 2) return "ride";
  if (explicit.includes("idle")) return "idle";
  if (acc === true) return "idle";
  return "park";
};

const buildHistoryRowsFromPlaybackPoints = (points = []) => {
  if (!Array.isArray(points) || !points.length) return [];

  const normalizedPoints = points
    .map((point) => {
      const timestampMs = toTimestampMs(point?.timestamp || point?.deviceTime || point?.serverTime || point?.createdAt);
      const coords = getPlaybackPointCoords(point);
      return {
        raw: point,
        timestampMs,
        coords,
        address: getPlaybackPointAddress(point),
        mode: getPlaybackPointMode(point),
      };
    })
    .filter((point) => point.timestampMs)
    .sort((left, right) => left.timestampMs - right.timestampMs);

  if (!normalizedPoints.length) return [];

  const rows = [];
  let segment = null;

  const pushSegment = () => {
    if (!segment) return;
    const durationMinutes = Math.max(0, Math.round((segment.end.timestampMs - segment.start.timestampMs) / 60000));
    const distanceKm = Number(segment.distanceKm || 0);
    rows.push({
      id: `${segment.start.timestampMs}-${segment.end.timestampMs}-${segment.mode}`,
      startTimeRaw: new Date(segment.start.timestampMs).toISOString(),
      endTimeRaw: new Date(segment.end.timestampMs).toISOString(),
      startLabel: formatHistoryDateLabel(segment.start.timestampMs),
      endLabel: formatHistoryDateLabel(segment.end.timestampMs),
      startAddress: segment.start.address || "NA",
      endAddress: segment.end.address || "NA",
      startCoords: segment.start.coords,
      endCoords: segment.end.coords,
      startAddressResolved: Boolean(segment.start.address && segment.start.address !== "NA"),
      endAddressResolved: Boolean(segment.end.address && segment.end.address !== "NA"),
      durationMinutes,
      durationText: formatDurationMins(durationMinutes),
      distanceText: formatHistoryDistance(distanceKm),
      isRide: segment.mode === "ride" && distanceKm > 0.03,
    });
    segment = null;
  };

  normalizedPoints.forEach((point, index) => {
    if (!segment) {
      segment = {
        mode: point.mode,
        start: point,
        end: point,
        distanceKm: 0,
      };
      return;
    }

    const gapMinutes = Math.max(0, (point.timestampMs - segment.end.timestampMs) / 60000);
    const distanceStepKm =
      segment.end.coords && point.coords ? historyPointDistanceKm(segment.end.coords, point.coords) : 0;
    const modeChanged = point.mode !== segment.mode;
    const largeGap = gapMinutes > 30;

    if (modeChanged || largeGap) {
      pushSegment();
      segment = {
        mode: point.mode,
        start: point,
        end: point,
        distanceKm: 0,
      };
      return;
    }

    segment.end = point;
    segment.distanceKm += distanceStepKm;

    if ((!segment.start.address || segment.start.address === "NA") && point.address && point.address !== "NA") {
      segment.start = { ...segment.start, address: point.address };
    }
  });

  pushSegment();
  return rows.filter((row) => {
    return row.isRide || Number(row.durationMinutes || 0) >= 1 || row.startAddressResolved || row.endAddressResolved;
  });
};

const getHistoryDeviceLabel = (device) =>
  device?.vehicleNumber ||
  device?.vehicleNo ||
  device?.registrationNo ||
  device?.vehicleRegistration ||
  device?.name ||
  device?.imei ||
  "";

const buildHistoryDeviceMatchMeta = (device, query = "") => {
  const normalizedQuery = safeLower(query).trim();
  const vehicle = safeLower(
    device?.vehicleNumber ||
      device?.vehicleNo ||
      device?.registrationNo ||
      device?.vehicleRegistration
  ).trim();
  const name = safeLower(device?.name).trim();
  const imei = safeLower(device?.imei).trim();
  const label = safeLower(getHistoryDeviceLabel(device)).trim();

  if (!normalizedQuery) {
    return { matches: true, score: 0, label: getHistoryDeviceLabel(device) };
  }

  const exactVehicle = Boolean(vehicle) && vehicle === normalizedQuery;
  const exactLabel = Boolean(label) && label === normalizedQuery;
  const exactName = Boolean(name) && name === normalizedQuery;
  const exactImei = Boolean(imei) && imei === normalizedQuery;

  const startsVehicle = Boolean(vehicle) && vehicle.startsWith(normalizedQuery);
  const startsLabel = Boolean(label) && label.startsWith(normalizedQuery);
  const startsName = Boolean(name) && name.startsWith(normalizedQuery);
  const startsImei = Boolean(imei) && imei.startsWith(normalizedQuery);

  const includesVehicle = Boolean(vehicle) && vehicle.includes(normalizedQuery);
  const includesLabel = Boolean(label) && label.includes(normalizedQuery);
  const includesName = Boolean(name) && name.includes(normalizedQuery);
  const includesImei = Boolean(imei) && imei.includes(normalizedQuery);

  const matches =
    exactVehicle ||
    exactLabel ||
    exactName ||
    exactImei ||
    startsVehicle ||
    startsLabel ||
    startsName ||
    startsImei ||
    includesVehicle ||
    includesLabel ||
    includesName ||
    includesImei;

  if (!matches) {
    return { matches: false, score: Number.POSITIVE_INFINITY, label: getHistoryDeviceLabel(device) };
  }

  let score = 11;
  if (exactVehicle) score = 0;
  else if (exactLabel) score = 1;
  else if (exactName) score = 2;
  else if (exactImei) score = 3;
  else if (startsVehicle) score = 4;
  else if (startsLabel) score = 5;
  else if (startsName) score = 6;
  else if (startsImei) score = 7;
  else if (includesVehicle) score = 8;
  else if (includesLabel) score = 9;
  else if (includesName) score = 10;

  return { matches: true, score, label: getHistoryDeviceLabel(device) };
};

const prettyNotificationEvent = (value) => {
  if (!value) return "Unknown Event";
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getNotificationCoords = (alarm) => {
  const lat = Number(
    alarm?.location?.latitude ??
    alarm?.location?.lat ??
    alarm?.latitude ??
    alarm?.lat
  );
  const lng = Number(
    alarm?.location?.longitude ??
    alarm?.location?.lng ??
    alarm?.longitude ??
    alarm?.lng
  );
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const formatNotificationTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const resolveSidebarCloudinaryIcon = (rawVehicleIcon, status) => {
  const normalizedType = String(rawVehicleIcon || "car").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const type = DEVICE_TYPE_ALIASES[normalizedType] || normalizedType;
  const color = STATUS_TO_ICON_COLOR[String(status || "").toLowerCase()] || "black";
  const typeIcons = CLOUDINARY_ICON_FILES[type] || CLOUDINARY_ICON_FILES.car;
  const file = typeIcons?.[color] || typeIcons?.black || CLOUDINARY_ICON_FILES.car?.[color] || CLOUDINARY_ICON_FILES.car?.black;
  if (!file) return null;
  return `${CLOUDINARY_BASE_URL}/${file}`;
};

// ─── IconCell (IGN / GPS / PWR column) ───────────────────────────────────────

const IconCell = ({ on, label, hidden = false, compact = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, minWidth: compact ? 20 : 24, visibility: hidden ? "hidden" : "visible" }}>
    <Box
      sx={{
        fontSize: compact ? 11 : 12,
        color: on ? "#16a34a" : "#9ca3af",
        lineHeight: 1,
        transform: on ? "scale(1.08)" : "scale(1)",
        transition: "all 0.15s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      >
      {label === "IGN" ? (
        <DirectionsCarFilledOutlinedIcon sx={{ fontSize: compact ? 12 : 14, color: on ? "#84cc16" : "#d1d5db" }} />
      ) : label === "GPS" ? (
        <WifiIcon sx={{ fontSize: compact ? 12 : 14, color: on ? "#16a34a" : "#9ca3af" }} />
      ) : (
        <PowerSettingsNewIcon sx={{ fontSize: compact ? 12 : 14, color: on ? "#16a34a" : "#ef4444" }} />
      )}
    </Box>
    <Typography
      sx={{
        fontSize: compact ? "7px" : "8px",
        fontWeight: 800,
        color: on ? "text.primary" : "#111827",
        opacity: on ? 1 : 0.78,
        lineHeight: 1,
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const getTelemetryIconState = ({ status, ignOn, gpsOn, pwrOn }) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (["no_data", "nodata", "expired"].includes(normalizedStatus)) {
    return { hidden: true, ignOn: false, gpsOn: false, pwrOn: false };
  }

  if (normalizedStatus === "running") {
    return { hidden: false, ignOn: true, gpsOn: gpsOn, pwrOn: true };
  }

  if (normalizedStatus === "idle") {
    return { hidden: false, ignOn: true, gpsOn: gpsOn, pwrOn: pwrOn };
  }

  if (normalizedStatus === "stopped") {
    return { hidden: false, ignOn: false, gpsOn: gpsOn, pwrOn: pwrOn };
  }

  if (normalizedStatus === "inactive") {
    return { hidden: false, ignOn, gpsOn, pwrOn: false };
  }

  return { hidden: false, ignOn, gpsOn, pwrOn };
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DeviceSidebar({
  devices = [],
  selectedDevices = [],
  focusedDeviceId = "",
  setSelectedDevices,
  deviceCounts = {},
  totalDeviceCount = 0,
  initialStatus = "all",
  setSearchTerm,
  searchTerm = "",
  onDeviceClick,
  onFocusDevice,
  onLoadedDevicesChange,
  socketUpdatesRef,
  onToggleVisibility,
  handleOpenCommandModal,
  onOpenGeofence,
  onOpenReport,
  getVehicleIcon,
  liveStatuses = {},
  open = true,
  onToggle,
  onEditDevice,
  onAddDevice,
  onSelectGeofence,
  selectedGeofenceId = null,
  sidebarGeofences = [],
  onCreateSidebarGeofence,
  onEditSidebarGeofence,
  onDeleteSidebarGeofence,
  onAssignSidebarGeofence,
  sidebarLandmarks = [],
  onSelectLandmark,
  selectedLandmarkId = null,
  onCreateSidebarLandmark,
  onUpdateSidebarLandmark,
  onDeleteSidebarLandmark,
  onAssignSidebarLandmark,
  landmarkSaving = false,
  currentContextDevice = null,
  onLandmarkDraftChange,
  landmarkDraftValue = null,
  onLandmarkFocusChange,
  onLandmarkPlacementModeChange,
  notifications = [],
  onSelectNotification,
  selectedNotificationId = null,
  onRefreshNotifications,
  notificationsRefreshing = false,
  onOpenPlayback,
  historySelection = null,
  onClosePlayback,
  mobileMode = false,
  progressiveLoading = {},
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactDesktopWidth = useMediaQuery("(max-width: 1536px)");
  const isCompactDesktopHeight = useMediaQuery("(max-height: 900px)");
  const compactMobile = mobileMode || isMobile;
  const compactDesktop = !compactMobile && (isCompactDesktopWidth || isCompactDesktopHeight);
  const desktopRowTemplate = compactDesktop
  ? "22px 22px minmax(118px, 2fr) 34px 22px 22px 22px 32px"
  : "28px 28px minmax(160px, 2fr) 40px 24px 24px 24px 36px";
  const { showStatus } = useStatus();
  const currentUser = useSelector((state) => state.auth?.user);
  const currentUserId = currentUser?._id;
  const currentUserRole = safeLower(currentUser?.role);

  // Direct map links use `?status=running`. Apply the URL immediately rather
  // than first rendering the unfiltered all-devices page.
  const [activeStatus, setActiveStatus] = useState(() =>
    normalizeSidebarStatus(getMapStatusFromUrl() || initialStatus)
  );
  const [activeTab, setActiveTab] = useState("Objects");
  const [openGroups, setOpenGroups] = useState({ ungrouped: true });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [menuDevice, setMenuDevice] = useState(null);
  const [isMenuInteractionLocked, setIsMenuInteractionLocked] = useState(false);
  const [notificationMenuAnchor, setNotificationMenuAnchor] = useState(null);
  const [notificationMenuPosition, setNotificationMenuPosition] = useState(null);
  const [menuNotification, setMenuNotification] = useState(null);
  const [geofenceMenuAnchor, setGeofenceMenuAnchor] = useState(null);
  const [landmarkMenuAnchor, setLandmarkMenuAnchor] = useState(null);
  const [menuLandmark, setMenuLandmark] = useState(null);
  const [remarkAlarm, setRemarkAlarm] = useState(null);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDevice, setShareDevice] = useState(null);
  const [sharePreset, setSharePreset] = useState("1");
  const [shareCustomHours, setShareCustomHours] = useState("1");
  const [shareLink, setShareLink] = useState("");

  useDevRenderInstrumentation("DeviceSidebar", () => ({
    devicesCount: Array.isArray(devices) ? devices.length : 0,
    selectedDevicesCount: Array.isArray(selectedDevices) ? selectedDevices.length : 0,
    notificationsCount: Array.isArray(notifications) ? notifications.length : 0,
    initialStatus,
    mobileMode: compactMobile,
  }));
  const [shareExpiresAt, setShareExpiresAt] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [historyDeviceDropdownOpen, setHistoryDeviceDropdownOpen] = useState(false);
  const [historyDeviceQuery, setHistoryDeviceQuery] = useState("");
  const [historyDeviceId, setHistoryDeviceId] = useState("");
  const [historyFilter, setHistoryFilter] = useState("Today");
  const [notificationEventFilters, setNotificationEventFilters] = useState([]);
  const [notificationEventFilterAnchor, setNotificationEventFilterAnchor] = useState(null);
  const [historyFromDate, setHistoryFromDate] = useState(() => toYMD(new Date()));
  const [historyToDate, setHistoryToDate] = useState(() => toYMD(new Date()));
  const [historyFromHour, setHistoryFromHour] = useState("12");
  const [historyFromMinute, setHistoryFromMinute] = useState("00");
  const [historyFromMeridiem, setHistoryFromMeridiem] = useState("AM");
  const [historyToHour, setHistoryToHour] = useState("11");
  const [historyToMinute, setHistoryToMinute] = useState("59");
  const [historyToMeridiem, setHistoryToMeridiem] = useState("PM");
  const [historyResults, setHistoryResults] = useState([]);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyExportAnchor, setHistoryExportAnchor] = useState(null);
  const [landmarkFormOpen, setLandmarkFormOpen] = useState(false);
  const [landmarkMode, setLandmarkMode] = useState("create");
  const [landmarkEditingId, setLandmarkEditingId] = useState(null);
  const [landmarkForm, setLandmarkForm] = useState({
    name: "",
    category: "others",
    address: "",
    description: "",
    latitude: null,
    longitude: null,
    radius: 30,
  });
  const [landmarkLocationQuery, setLandmarkLocationQuery] = useState("");
  const [landmarkLocationLoading, setLandmarkLocationLoading] = useState(false);
  const [landmarkFormError, setLandmarkFormError] = useState("");
  const [landmarkPlacementActive, setLandmarkPlacementActive] = useState(false);
  const [landmarkSearchResults, setLandmarkSearchResults] = useState([]);
  const [landmarkSearchLoading, setLandmarkSearchLoading] = useState(false);
  const [landmarkSearchOpen, setLandmarkSearchOpen] = useState(false);
  const { openModal: openQuickModal } = useQuickModal();
  const lastAutoPlaybackKeyRef = useRef("");

  const sidebarOpen = open;
  const setSidebarOpen = onToggle || (() => {});

  const scrollRef = useRef(null);
  const objectQuerySeqRef = useRef(0);
  const trackingStateByDeviceRef = useRef(new Map());
  const historyImportInputRef = useRef(null);
  const historyDeviceDropdownRef = useRef(null);
  const landmarkSearchDropdownRef = useRef(null);
  const landmarkRadiusSyncSourceRef = useRef("idle");
  const lastAppliedHistorySelectionRef = useRef("");
  const lastFetchedHistorySelectionRef = useRef("");
  const pendingHistoryPlaybackTargetRef = useRef(null);
  const statusPillsRef = useRef(null);
  const [objectsListHeight, setObjectsListHeight] = useState(480);
  const [pagedObjectDevices, setPagedObjectDevices] = useState([]);
  const [pagedObjectsPage, setPagedObjectsPage] = useState(0);
  const [pagedObjectsHasMore, setPagedObjectsHasMore] = useState(false);
  // Start in loading state so the first paint never briefly says "No devices"
  // before the initial page request is scheduled.
  const [pagedObjectsLoading, setPagedObjectsLoading] = useState(true);
  // Match the tracking view's initial behaviour: the first paginated batch is
  // selected immediately, and every later page remains selected while this
  // mode is active.  An explicit individual uncheck turns this mode off.
  const [selectAllLoaded, setSelectAllLoaded] = useState(true);
  const [localDeviceCounts, setLocalDeviceCounts] = useState({});
  const [loadDevicesPage] = useLazyGetCompactFleetDevicesQuery();
  const [updateAlarmRemark, { isLoading: savingRemark }] = useUpdateAlarmRemarkMutation();
  const sidebarPageCacheParams = useMemo(
    () => ({
      status: activeStatus,
      search: String(searchTerm || "").trim(),
      limit: SIDEBAR_OBJECTS_PAGE_SIZE,
    }),
    [activeStatus, searchTerm]
  );

  const scrollStatusPills = useCallback((direction) => {
    const node = statusPillsRef.current;
    if (!node) return;
    const delta = Math.max(120, Math.floor((node.clientWidth || 0) * 0.55));
    node.scrollBy({
      left: direction === "left" ? -delta : delta,
      behavior: "smooth",
    });
  }, []);

  const resetHistoryRangeToToday = useCallback(() => {
    const today = toYMD(new Date());
    setHistoryFilter("Today");
    setHistoryFromDate(today);
    setHistoryToDate(today);
    setHistoryFromHour("12");
    setHistoryFromMinute("00");
    setHistoryFromMeridiem("AM");
    setHistoryToHour("11");
    setHistoryToMinute("59");
    setHistoryToMeridiem("PM");
  }, []);

  const markHistoryFilterCustom = useCallback(() => {
    setHistoryFilter("Custom");
  }, []);

  useEffect(() => {
    if (activeTab !== "Objects") return;

    const cachedPayload = readDeviceViewCache(DEVICE_SIDEBAR_PAGE_CACHE_SCOPE, {
      userId: currentUserId,
      params: sidebarPageCacheParams,
    });
    if (!Array.isArray(cachedPayload?.rows) || !cachedPayload.rows.length) return;

    setPagedObjectDevices(cachedPayload.rows);
    setPagedObjectsPage(Number(cachedPayload.page || 1) || 1);
    setPagedObjectsHasMore(Boolean(cachedPayload.hasMore));
    // Rows are safe to restore for a fast first paint, but status totals are
    // live fleet data. Restoring a five-minute-old total here made the map
    // disagree with Dashboard after new packets arrived.
  }, [activeTab, currentUserId, sidebarPageCacheParams]);

  useEffect(() => {
    setActiveStatus(normalizeSidebarStatus(getMapStatusFromUrl() || initialStatus));
  }, [initialStatus]);

  const { data: groupsResponse } = useGetAllGroupsUnpagedQuery(
    { limit: 500, sortBy: "createdAt", sortOrder: "desc" },
    { refetchOnMountOrArgChange: true }
  );
  const groups = useMemo(() => groupsResponse?.data || [], [groupsResponse]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.max(260, Number(node.clientHeight || 0));
      setObjectsListHeight(nextHeight);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(node);
    window.addEventListener("resize", updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const socketCtx = useContext(SocketContext);
  const connected = socketCtx?.connected;
  const getDeviceSocketData = socketCtx?.getDeviceSocketData;
  const onSocketEvent = socketCtx?.on;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      if (isMenuInteractionLocked) return;
      setTick((t) => t + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [isMenuInteractionLocked]);

  useEffect(() => {
    if (!onSocketEvent) return undefined;
    // Coalesce socket bursts to one re-render every 2s. Row live-data is read
    // from socketUpdatesRef at render time, so a missed bump just delays a
    // status pill / address refresh — never a map marker.
    let timer = null;
    let pending = false;
    const bump = () => {
      pending = true;
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        if (pending) {
          pending = false;
          if (isMenuInteractionLocked) return;
          setTick((t) => t + 1);
        }
      }, 2000);
    };
    const off = [
      onSocketEvent("gps_packet", bump),
      onSocketEvent("location_update", bump),
      onSocketEvent("gps_update", bump),
      onSocketEvent("status_update", bump),
      onSocketEvent("vehicle_update", bump),
      onSocketEvent("device_update", bump),
    ];
    return () => {
      off.forEach((fn) => fn?.());
      if (timer) clearTimeout(timer);
    };
  }, [isMenuInteractionLocked, onSocketEvent]);

  const allDevicesList = useMemo(() => {
    if (Array.isArray(devices)) return devices;
    if (Array.isArray(devices?.data)) return devices.data;
    return [];
  }, [devices]);

  const getLiveStatusInput = useCallback((device) => {
    const byRef = socketUpdatesRef?.current?.[device?._id] || socketUpdatesRef?.current?.[device?.imei] || {};
    const byHook = getDeviceSocketData ? getDeviceSocketData(device?._id || device?.imei) : {};
    const byProp = liveStatuses?.[device?._id] || liveStatuses?.[device?.imei] || {};
    // Match MapContainer exactly. `socketUpdatesRef` is the map's live packet
    // source; comparing it with secondary caches here could select an older
    // status response that happened to have a newer UI arrival timestamp.
    if (Object.keys(byRef).length) return byRef;
    if (Object.keys(byHook).length) return byHook;
    return byProp;
  }, [socketUpdatesRef, getDeviceSocketData, liveStatuses]);

  const normalizedSidebarQuery = useMemo(
    () => safeLower(searchTerm).trim(),
    [searchTerm]
  );

  const deviceDerivedMeta = useMemo(() => {
    void tick;
    const byId = new Map();
    const byGroupId = new Map();
    const statusCounts = {
      all: 0,
      running: 0,
      idle: 0,
      stopped: 0,
      inactive: 0,
      expired: 0,
      no_data: 0,
    };

    (Array.isArray(allDevicesList) ? allDevicesList : []).forEach((device) => {
      const deviceId = String(device?._id || "");
      const liveInput = getLiveStatusInput(device);
      const resolvedStatus = getBackendSidebarStatus(device, liveInput);
      const meta = {
        liveInput,
        status: resolvedStatus,
        liveVersion: getSidebarLiveRenderSignature(liveInput, resolvedStatus),
        name: safeLower(device?.name),
        imei: String(device?.imei || ""),
        vehicleNumber: safeLower(device?.vehicleNumber),
      };

      if (deviceId) {
        byId.set(deviceId, meta);
      }

      statusCounts.all += 1;
      statusCounts[resolvedStatus] = (statusCounts[resolvedStatus] || 0) + 1;
    });

    (Array.isArray(groups) ? groups : []).forEach((group) => {
      const ids = new Set(
        (Array.isArray(group?.devices) ? group.devices : [])
          .map((entry) => (typeof entry === "string" ? entry : entry?._id))
          .filter(Boolean)
          .map((value) => String(value))
      );
      byGroupId.set(String(group?._id || ""), ids);
    });

    return {
      byId,
      byGroupId,
      statusCounts,
    };
  }, [allDevicesList, getLiveStatusInput, groups, tick]);

  const formatLandmarkCategoryLabel = useCallback(
    (value) =>
      String(value || "others")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    []
  );

  const resolveContextCoordinates = useCallback(() => {
    const live = currentContextDevice ? getLiveStatusInput(currentContextDevice) : {};
    const lat = Number(
      live?.latitude ??
      live?.lat ??
      currentContextDevice?.currentLocation?.latitude ??
      currentContextDevice?.liveData?.latitude ??
      currentContextDevice?.lat ??
      currentContextDevice?.latitude
    );
    const lng = Number(
      live?.longitude ??
      live?.lng ??
      currentContextDevice?.currentLocation?.longitude ??
      currentContextDevice?.liveData?.longitude ??
      currentContextDevice?.lng ??
      currentContextDevice?.longitude
    );

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [currentContextDevice, getLiveStatusInput]);

  const reverseGeocodeCoordinates = useCallback(async (lat, lng) => {
    const response = await api.get("/geocode/reverse", {
      params: { lat, lon: lng },
    });
    return String(response?.data?.address || `${lat}, ${lng}`).trim();
  }, []);

  const searchLandmarkLocations = useCallback(async (query) => {
    const response = await api.get("/geocode/search", {
      params: { q: query },
    });
    return Array.isArray(response?.data?.results) ? response.data.results : [];
  }, []);

  const hydrateLandmarkLocation = useCallback(async (preferredLocation = null) => {
    setLandmarkLocationLoading(true);
    setLandmarkFormError("");
    try {
      let coords =
        Number.isFinite(Number(preferredLocation?.latitude)) &&
        Number.isFinite(Number(preferredLocation?.longitude))
          ? {
              lat: Number(preferredLocation.latitude),
              lng: Number(preferredLocation.longitude),
            }
          : null;

      if (!coords && typeof navigator !== "undefined" && navigator.geolocation) {
        coords = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                lat: Number(position.coords.latitude),
                lng: Number(position.coords.longitude),
              });
            },
            reject,
            {
              enableHighAccuracy: true,
              timeout: 8000,
              maximumAge: 30000,
            }
          );
        });
      }

      if (!coords) {
        coords = resolveContextCoordinates();
      }

      if (!Number.isFinite(Number(coords?.lat)) || !Number.isFinite(Number(coords?.lng))) {
        throw new Error("Current location not available.");
      }

      const address =
        String(preferredLocation?.address || "").trim() ||
        (await reverseGeocodeCoordinates(coords.lat, coords.lng));
      setLandmarkForm((prev) => ({
        ...prev,
        name: prev.name || String(preferredLocation?.name || "").trim(),
        address,
        description: prev.description || address,
        latitude: coords.lat,
        longitude: coords.lng,
      }));
      setLandmarkLocationQuery(address);
      return coords;
    } catch (error) {
      const message = error?.message || "Unable to detect current location.";
      setLandmarkFormError(message);
      throw error;
    } finally {
      setLandmarkLocationLoading(false);
    }
  }, [resolveContextCoordinates, reverseGeocodeCoordinates]);

  const matchesActiveSidebarStatus = useCallback((device) => {
    if (activeStatus === "all") return true;
    const live = getLiveStatusInput(device);
    return getBackendSidebarStatus(device, live) === activeStatus;
  }, [activeStatus, getLiveStatusInput]);

  const fetchObjectDevicesPage = useCallback(async ({ page = 1, reset = false } = {}) => {
    if (activeTab !== "Objects") return;
    if (pagedObjectsLoading && !reset) return;

    const requestSeq = reset ? objectQuerySeqRef.current + 1 : objectQuerySeqRef.current;
    if (reset) objectQuerySeqRef.current = requestSeq;

    setPagedObjectsLoading(true);
    try {
  let nextPage = page;
      let hasMore = false;
      let mergedRows = reset ? [] : [...pagedObjectDevices];
      // A status tab is already filtered by the same backend aggregation that
      // supplies the status-pill totals. Filtering it again with a partial
      // socket/client cache caused valid rows to disappear (often leaving 1).
      let matchedRows = mergedRows;
      const minimumVisibleMatches = activeStatus === "all" ? SIDEBAR_OBJECTS_PAGE_SIZE : 30;

      do {
        const payload = await loadDevicesPage(
          {
  page: nextPage,
  limit: SIDEBAR_OBJECTS_PAGE_SIZE,
  includeRelations: "false",
  includeExpired: "true",
  ...(activeStatus !== "all" ? { status: activeStatus } : {}),
  ...(searchTerm?.trim() ? { search: searchTerm.trim() } : {}),
},
          false
        ).unwrap();

        if (requestSeq !== objectQuerySeqRef.current) return;

        const rows = Array.isArray(payload?.data) ? payload.data : [];
if (payload?.counts) {
  setLocalDeviceCounts(payload.counts);
}
        mergedRows = nextPage === 1 && reset ? rows : [...mergedRows, ...rows];
        matchedRows = mergedRows;
        hasMore = Boolean(payload?.hasMore);
        nextPage = Number(payload?.page || nextPage);

        if (!reset) break;
        if (activeStatus === "all" && mergedRows.length >= SIDEBAR_OBJECTS_PAGE_SIZE) break;
        if (matchedRows.length >= minimumVisibleMatches) break;
        if (!hasMore) break;
        nextPage += 1;
      } while (hasMore);

      if (
        reset &&
        mergedRows.length === 0 &&
        ["user", "client", "viewer"].includes(currentUserRole)
      ) {
        const localFallbackRows = (Array.isArray(allDevicesList) ? allDevicesList : []).filter((device) => {
          const query = String(searchTerm || "").trim().toLowerCase();
          if (activeStatus !== "all" && !matchesActiveSidebarStatus(device)) return false;
          if (!query) return true;
          return [
            device?.name,
            device?.vehicleNumber,
            device?.imei,
            device?.msdn,
          ].some((value) => String(value || "").toLowerCase().includes(query));
        });

        mergedRows = localFallbackRows.slice(0, SIDEBAR_OBJECTS_PAGE_SIZE);
        matchedRows = mergedRows;
        hasMore = localFallbackRows.length > mergedRows.length;
        nextPage = mergedRows.length ? 1 : 0;
      }

      setPagedObjectDevices(mergedRows);
      setPagedObjectsPage(nextPage);
      setPagedObjectsHasMore(hasMore);
      writeDeviceViewCache(DEVICE_SIDEBAR_PAGE_CACHE_SCOPE, {
        userId: currentUserId,
        params: sidebarPageCacheParams,
       payload: {
  rows: mergedRows,
  page: nextPage,
  hasMore,
},
      });
    } catch (error) {
      if (reset) {
        setPagedObjectDevices([]);
        setPagedObjectsPage(0);
        setPagedObjectsHasMore(false);
      }
    } finally {
      if (requestSeq === objectQuerySeqRef.current) {
        setPagedObjectsLoading(false);
      }
    }
  }, [
    activeStatus,
    activeTab,
    allDevicesList,
    currentUserId,
    currentUserRole,
    loadDevicesPage,
    matchesActiveSidebarStatus,
    pagedObjectDevices,
    pagedObjectsLoading,
    searchTerm,
    sidebarPageCacheParams,
  ]);

// Search + status filtering shared by list and count pills
  const filterList = useCallback((list) => {
    let result = Array.isArray(list) ? list : [];
    if (normalizedSidebarQuery) {
      result = result.filter((device) => {
        const meta = deviceDerivedMeta.byId.get(String(device?._id || ""));
        const name = meta?.name ?? safeLower(device?.name);
        const imei = meta?.imei ?? String(device?.imei || "");
        const vehicleNo = meta?.vehicleNumber ?? safeLower(device?.vehicleNumber);
        return (
          name.includes(normalizedSidebarQuery) ||
          imei.includes(normalizedSidebarQuery) ||
          vehicleNo.includes(normalizedSidebarQuery)
        );
      });
    }
    // For a loaded status tab, the server has already applied the canonical
    // filter. Re-applying a client-side status resolver to this page can drop
    // rows while a socket update is in flight.
    const usingServerFilteredPage =
      activeStatus !== "all" && Array.isArray(pagedObjectDevices) && pagedObjectDevices.length > 0;
    if (activeStatus !== "all" && !usingServerFilteredPage) {
      result = result.filter((device) => {
        const meta = deviceDerivedMeta.byId.get(String(device?._id || ""));
        return (meta?.status || "no_data") === activeStatus;
      });
    }
    return result;
  }, [activeStatus, deviceDerivedMeta.byId, normalizedSidebarQuery, pagedObjectDevices]);

  const deviceMatchesSidebarQuery = useCallback((device) => {
    if (!normalizedSidebarQuery) return true;
    const meta = deviceDerivedMeta.byId.get(String(device?._id || ""));
    const name = meta?.name ?? safeLower(device?.name);
    const imei = meta?.imei ?? String(device?.imei || "");
    const vehicleNo = meta?.vehicleNumber ?? safeLower(device?.vehicleNumber);
    return (
      name.includes(normalizedSidebarQuery) ||
      imei.includes(normalizedSidebarQuery) ||
      vehicleNo.includes(normalizedSidebarQuery)
    );
  }, [deviceDerivedMeta.byId, normalizedSidebarQuery]);

  const deviceMatchesSidebarStatus = useCallback((device) => {
    if (activeStatus === "all") return true;
    const meta = deviceDerivedMeta.byId.get(String(device?._id || ""));
    return (meta?.status || "no_data") === activeStatus;
  }, [activeStatus, deviceDerivedMeta.byId]);

  const getSidebarRelatedDevice = useCallback((entity = {}) => {
    const possibleIds = [
      entity?.deviceId,
      entity?.device?._id,
      entity?._id,
    ]
      .filter(Boolean)
      .map((value) => String(value));
    const possibleImeis = [
      entity?.imei,
      entity?.device?.imei,
    ]
      .filter(Boolean)
      .map((value) => String(value));
    const possibleNames = [
      entity?.deviceName,
      entity?.device?.name,
      entity?.vehicleNumber,
      entity?.device?.vehicleNumber,
      entity?.name,
    ]
      .filter(Boolean)
      .map((value) => safeLower(value));

    return allDevicesList.find((device) => {
      const deviceId = String(device?._id || "");
      const deviceImei = String(device?.imei || "");
      const deviceName = safeLower(device?.name || "");
      const vehicleNo = safeLower(device?.vehicleNumber || "");
      return (
        possibleIds.includes(deviceId) ||
        possibleImeis.includes(deviceImei) ||
        possibleNames.includes(deviceName) ||
        possibleNames.includes(vehicleNo)
      );
    }) || null;
  }, [allDevicesList]);

  const fetchObjectDevicesPageRef = useRef(fetchObjectDevicesPage);
  useEffect(() => {
    fetchObjectDevicesPageRef.current = fetchObjectDevicesPage;
  }, [fetchObjectDevicesPage]);

  useEffect(() => {
    if (activeTab !== "Objects") return undefined;
    const timer = setTimeout(() => {
      fetchObjectDevicesPageRef.current?.({ page: 1, reset: true });
    }, searchTerm?.trim() ? 220 : 20);
    return () => clearTimeout(timer);
    // Deliberately do not depend on the callback: it changes while a page is
    // merged, and previously retriggered duplicate reset requests.
  }, [activeStatus, activeTab, searchTerm]);

  const objectSourceDevices = useMemo(() => {
    if (activeTab === "Objects") {
      const pagedRows = Array.isArray(pagedObjectDevices) ? pagedObjectDevices : [];
      const fullRows = Array.isArray(allDevicesList) ? allDevicesList : [];

      if (pagedRows.length > 0) {
        return pagedRows;
      }
      return fullRows;
    }
    return allDevicesList;
  }, [activeTab, allDevicesList, pagedObjectDevices]);

  const visibleFilteredDevices = useMemo(() => {
    if (activeTab !== "Objects") return [];
    return filterList(objectSourceDevices);
  }, [activeTab, objectSourceDevices, activeStatus, searchTerm, tick]);

  useEffect(() => {
    if (activeTab !== "Objects") return;
    onLoadedDevicesChange?.(pagedObjectDevices);
  }, [activeTab, onLoadedDevicesChange, pagedObjectDevices]);

  useEffect(() => {
    // Do not select the legacy full-list fallback while the compact first page
    // is still loading; only the actual paginated rows belong on the map.
    if (
      !selectAllLoaded ||
      pagedObjectDevices.length === 0 ||
      visibleFilteredDevices.length === 0
    ) {
      return;
    }

    const loadedIds = visibleFilteredDevices.map((device) => device._id).filter(Boolean);
    setSelectedDevices?.((currentSelection = []) => {
      const selectedIds = new Set(currentSelection.map((id) => String(id)));
      const missingIds = loadedIds.filter((id) => !selectedIds.has(String(id)));
      return missingIds.length ? [...currentSelection, ...missingIds] : currentSelection;
    });
  }, [pagedObjectDevices.length, selectAllLoaded, setSelectedDevices, visibleFilteredDevices]);

  const statusCounts = useMemo(() => {
    const hasStatusBreakdown = (counts = {}) => {
      const keys = [
        "running",
        "idle",
        "idling",
        "stopped",
        "inactive",
        "offline",
        "expired",
        "expiredCount",
        "no_data",
        "nodata",
      ];
      return keys.some(
        (key) => Object.prototype.hasOwnProperty.call(counts || {}, key)
      );
    };

    const apiCounts = hasStatusBreakdown(deviceCounts) ? deviceCounts : null;
    const pageCounts = hasStatusBreakdown(localDeviceCounts) ? localDeviceCounts : null;
    // Dashboard summary is the single fresh fleet-wide count source. The
    // paginated endpoint is used only for sidebar rows; its prior cached
    // response was able to overwrite fresh Dashboard totals with zeroes.
    const sourceCounts = apiCounts || pageCounts || {};
    const hasAuthoritativeStatusCounts = Boolean(apiCounts || pageCounts);
    const totalSourceCounts = apiCounts || pageCounts || {};

    // `allDevicesList` is progressively hydrated (first chunk can be 500).
    // It is not a fleet total, so never use it for the status pills. Showing
    // that partial chunk briefly and then replacing it with the real total was
    // the initial-count flicker seen on the live map.
    const authoritativeTotal = hasAuthoritativeStatusCounts
      ? Math.max(
          Number(totalSourceCounts?.total) || 0,
          Number(totalSourceCounts?.all) || 0,
          Number(sourceCounts?.total) || 0,
          Number(sourceCounts?.all) || 0,
          Number(totalDeviceCount) || 0
        )
      : 0;

    const running = Number(sourceCounts?.running || 0);
    const idle = Number(sourceCounts?.idle || sourceCounts?.idling || 0);
    const inactive = Number(sourceCounts?.inactive || sourceCounts?.offline || 0);
    const expired = Number(sourceCounts?.expired || sourceCounts?.expiredCount || 0);
    const noData = Number(sourceCounts?.no_data ?? sourceCounts?.nodata ?? 0);
    const stopped =
      typeof sourceCounts?.stopped !== "undefined"
        ? Number(sourceCounts.stopped || 0)
        : hasAuthoritativeStatusCounts
          ? Math.max(0, authoritativeTotal - running - idle - inactive - expired - noData)
          : 0;

    return {
      all: authoritativeTotal,
      running,
      idle,
      stopped,
      inactive,
      expired,
      no_data: noData,
      isReady: hasAuthoritativeStatusCounts,
    };
  }, [
    deviceCounts,
    deviceDerivedMeta,
    localDeviceCounts,
    totalDeviceCount,
  ]);

  const groupedDevices = useMemo(() => {
    const grouped = {};
    // Grouping must use loaded sidebar pages, otherwise every map row gets
    // rendered and InfiniteLoader pagination is bypassed.
    const groupingSourceDevices = Array.isArray(objectSourceDevices) ? objectSourceDevices : [];

    (Array.isArray(groups) ? groups : []).forEach((group) => {
      grouped[group._id] = {
        name: group?.name || "Group",
        devices: [],
      };
    });

    groupingSourceDevices.forEach((device) => {
      const deviceId = String(device?._id || "");
      if (!deviceId) return;

      let assignedGroupKey = null;
      for (const group of groups) {
        const ids = deviceDerivedMeta.byGroupId.get(String(group?._id || ""));
        if (ids?.has(deviceId)) {
          assignedGroupKey = group._id;
          break;
        }
      }

      if (assignedGroupKey) {
        grouped[assignedGroupKey]?.devices.push(device);
        return;
      }

      if (!grouped.ungrouped) {
        grouped.ungrouped = { name: "Ungrouped", devices: [] };
      }
      grouped.ungrouped.devices.push(device);
    });

    if (!grouped.ungrouped) {
      grouped.ungrouped = { name: "Ungrouped", devices: [] };
    }

    return grouped;
  }, [deviceDerivedMeta.byGroupId, groups, objectSourceDevices]);

  const progressiveStatusText = useMemo(() => {
    const activeSegments = [];
    if (progressiveLoading?.runningIdle) activeSegments.push("Running/Idle");
    if (progressiveLoading?.stopped) activeSegments.push("Stopped");
    if (progressiveLoading?.inactive) activeSegments.push("No Reporting");
    return activeSegments.length ? `Loading ${activeSegments.join(" • ")}` : "";
  }, [progressiveLoading]);

  const geofenceItems = useMemo(() => {
    const query = safeLower(searchTerm).trim();

    if (Array.isArray(sidebarGeofences) && sidebarGeofences.length) {
      const rows = sidebarGeofences.map((item, index) => ({
        id: String(item?.id || item?._id || `shared-geofence-${index}`),
        _id: item?._id || item?.id || null,
        name: item?.name || `Geofence ${index + 1}`,
        type: item?.type || (item?.radius ? "circle" : "polygon"),
        category: item?.category || "",
        locationLabel: item?.locationLabel || "",
        assignedCount: Number(item?.assignedCount || item?.assignedDeviceIds?.length || 0),
        assignedDeviceIds: Array.isArray(item?.assignedDeviceIds) ? item.assignedDeviceIds : [],
        radiusText: item?.radiusText || "",
        raw: item,
      }));

      return rows.filter((item) => {
        const matchesQuery = !query ||
          safeLower(item.name).includes(query) ||
          safeLower(item.category).includes(query) ||
          safeLower(item.locationLabel).includes(query);
        if (!matchesQuery) return false;
        if (activeStatus === "all") return true;
        const assignedDevices = item.assignedDeviceIds
          .map((deviceId) => getSidebarRelatedDevice({ deviceId }))
          .filter(Boolean);
        return assignedDevices.some(deviceMatchesSidebarStatus);
      });
    }

    const items = [];
    allDevicesList.forEach((device) => {
      const deviceGeofences = Array.isArray(device?.geofences) ? device.geofences : [];
      deviceGeofences.forEach((geofence, index) => {
        if (geofence?.isActive === false) return;
        const geofenceId =
          geofence?.sourceGeofenceId ||
          geofence?._id ||
          geofence?.id ||
          `${device?._id || device?.imei || "device"}-${geofence?.name || "geofence"}-${index}`;
        items.push({
          id: String(geofenceId),
          name: geofence?.name || `Geofence ${index + 1}`,
          type: geofence?.type || (geofence?.radius ? "circle" : "polygon"),
          deviceId: device?._id,
          deviceName: device?.name || device?.vehicleNumber || "Unnamed device",
          category: geofence?.category || "",
          locationLabel: geofence?.locationLabel || "",
        });
      });
    });

    return items.filter((item) => {
      const matchesQuery = !query ||
        safeLower(item.name).includes(query) ||
        safeLower(item.deviceName).includes(query) ||
        safeLower(item.category).includes(query) ||
        safeLower(item.locationLabel).includes(query);
      if (!matchesQuery) return false;
      if (activeStatus === "all") return true;
      const relatedDevice = getSidebarRelatedDevice(item);
      return relatedDevice ? deviceMatchesSidebarStatus(relatedDevice) : false;
    });
  }, [activeStatus, allDevicesList, deviceMatchesSidebarStatus, getSidebarRelatedDevice, searchTerm, sidebarGeofences]);

  const landmarkItems = useMemo(() => {
    const query = safeLower(searchTerm).trim();
    return (Array.isArray(sidebarLandmarks) ? sidebarLandmarks : [])
      .map((item, index) => ({
        id: String(item?.id || item?._id || `landmark-${index}`),
        _id: item?._id || item?.id || null,
        name: item?.name || `Landmark ${index + 1}`,
        radius: Number(item?.radius ?? item?.radiusMeters ?? 30),
        radiusText: Number(item?.radius ?? item?.radiusMeters ?? 30).toFixed(2),
        category: item?.category || "others",
        address: item?.address || "",
        description: item?.description || "",
        latitude: Number(item?.latitude ?? item?.lat),
        longitude: Number(item?.longitude ?? item?.lng),
        assignedCount: Number(
          item?.assignedCount ??
            (Array.isArray(item?.assignedDevices) ? item.assignedDevices.length : 0)
        ),
        assignedDevices: Array.isArray(item?.assignedDevices) ? item.assignedDevices : [],
        raw: item,
      }))
      .filter((item) => {
        const matchesQuery = !query || (
          safeLower(item.name).includes(query) ||
          safeLower(item.category).includes(query) ||
          safeLower(item.address).includes(query)
        );
        if (!matchesQuery) return false;
        if (activeStatus === "all") return true;
        const assignedDevices = item.assignedDevices
          .map((device) => getSidebarRelatedDevice(device))
          .filter(Boolean);
        return assignedDevices.some(deviceMatchesSidebarStatus);
      });
  }, [activeStatus, deviceMatchesSidebarStatus, getSidebarRelatedDevice, searchTerm, sidebarLandmarks]);

  const closeLandmarkMenu = useCallback(() => {
    setLandmarkMenuAnchor(null);
    setMenuLandmark(null);
  }, []);

  const openLandmarkMenu = useCallback((event, landmark) => {
    event.stopPropagation();
    setLandmarkMenuAnchor(event.currentTarget);
    setMenuLandmark(landmark);
  }, []);

  const openCreateLandmarkForm = useCallback(async (preferredLocation = null) => {
    setActiveTab("Landmark");
    setLandmarkMode("create");
    setLandmarkEditingId(null);
    landmarkRadiusSyncSourceRef.current = "idle";
    setLandmarkPlacementActive(true);
    onLandmarkDraftChange?.(null);
    setLandmarkForm({
      name: "",
      category: "others",
      address: "",
      description: "",
      latitude: null,
      longitude: null,
      radius: 30,
    });
    setLandmarkLocationQuery("");
    setLandmarkFormError("");
    setLandmarkSearchResults([]);
    setLandmarkSearchOpen(false);
    setLandmarkFormOpen(true);

    try {
      await hydrateLandmarkLocation(preferredLocation);
    } catch (_) {
      // Inline error already set.
    }
  }, [hydrateLandmarkLocation, onLandmarkDraftChange]);

  const openEditLandmarkForm = useCallback((landmark) => {
    if (!landmark) return;
    setActiveTab("Landmark");
    setLandmarkMode("edit");
    setLandmarkEditingId(String(landmark?._id || landmark?.id || ""));
    landmarkRadiusSyncSourceRef.current = "idle";
    setLandmarkPlacementActive(false);
    setLandmarkForm({
      name: String(landmark?.name || "").trim(),
      category: String(landmark?.category || "others").trim() || "others",
      address: String(landmark?.address || "").trim(),
      description: String(landmark?.description || "").trim(),
      latitude: Number(landmark?.latitude ?? landmark?.lat),
      longitude: Number(landmark?.longitude ?? landmark?.lng),
      radius: Number(landmark?.radius ?? landmark?.radiusMeters ?? 30),
    });
    setLandmarkLocationQuery(String(landmark?.address || "").trim());
    setLandmarkFormError("");
    setLandmarkSearchResults([]);
    setLandmarkSearchOpen(false);
    setLandmarkFormOpen(true);
  }, []);

  const closeLandmarkForm = useCallback(() => {
    setLandmarkFormOpen(false);
    setLandmarkMode("create");
    setLandmarkEditingId(null);
    landmarkRadiusSyncSourceRef.current = "idle";
    setLandmarkPlacementActive(false);
    onLandmarkDraftChange?.(null);
    setLandmarkFormError("");
    setLandmarkLocationQuery("");
    setLandmarkSearchResults([]);
    setLandmarkSearchOpen(false);
  }, [onLandmarkDraftChange]);

  useEffect(() => {
    if (typeof onLandmarkPlacementModeChange !== "function") return;
    onLandmarkPlacementModeChange(Boolean(landmarkFormOpen && landmarkPlacementActive));
  }, [landmarkFormOpen, landmarkPlacementActive, onLandmarkPlacementModeChange]);

  const handleLandmarkSubmit = useCallback(async () => {
    const name = String(landmarkForm?.name || "").trim();
    const latitude = Number(landmarkDraftValue?.latitude ?? landmarkForm?.latitude);
    const longitude = Number(landmarkDraftValue?.longitude ?? landmarkForm?.longitude);
    const radiusValue = Number(landmarkDraftValue?.radius ?? landmarkForm?.radius ?? 30);

    if (!name) {
      setLandmarkFormError("Landmark name is required.");
      return;
    }

    if (landmarkMode === "create" && !landmarkDraftValue) {
      setLandmarkFormError("Please draw circle on map first.");
      return;
    }

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      setLandmarkFormError("Current location is required for landmark.");
      return;
    }

    const payload = {
      name,
      category: String(landmarkForm?.category || "others").trim() || "others",
      address: String(landmarkForm?.address || "").trim(),
      description: String(landmarkForm?.description || "").trim(),
      latitude,
      longitude,
      radius: Math.max(0, radiusValue),
    };

    try {
      let savedLandmark = null;
      if (landmarkMode === "edit" && landmarkEditingId) {
        savedLandmark = await onUpdateSidebarLandmark?.({
          landmarkId: landmarkEditingId,
          ...payload,
        });
      } else {
        savedLandmark = await onCreateSidebarLandmark?.(payload);
      }
      if (savedLandmark && typeof onSelectLandmark === "function") {
        onSelectLandmark(savedLandmark);
      }
      closeLandmarkForm();
    } catch (error) {
      setLandmarkFormError(error?.message || "Failed to save landmark.");
    }
  }, [
    closeLandmarkForm,
    landmarkEditingId,
    landmarkDraftValue,
    landmarkForm,
    landmarkMode,
    onCreateSidebarLandmark,
    onSelectLandmark,
    onUpdateSidebarLandmark,
  ]);

  const handleLandmarkSuggestionSelect = useCallback((result) => {
    const lat = Number(result?.lat);
    const lng = Number(result?.lng);
    const address = String(result?.address || "").trim();
    const shortName = address.split(",")[0]?.trim() || "";

    setLandmarkForm((prev) => ({
      ...prev,
      name: prev.name || shortName,
      address,
      description: address,
      latitude: Number.isFinite(lat) ? lat : prev.latitude,
      longitude: Number.isFinite(lng) ? lng : prev.longitude,
    }));
    setLandmarkPlacementActive(true);
    setLandmarkLocationQuery(address);
    setLandmarkSearchOpen(false);
    setLandmarkSearchResults([]);
  }, []);

  useEffect(() => {
    if (!landmarkFormOpen || !landmarkLocationQuery?.trim()) {
      setLandmarkSearchResults([]);
      setLandmarkSearchOpen(false);
      setLandmarkSearchLoading(false);
      return undefined;
    }

    const query = String(landmarkLocationQuery || "").trim();
    if (query.length < 2) {
      setLandmarkSearchResults([]);
      setLandmarkSearchOpen(false);
      setLandmarkSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLandmarkSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchLandmarkLocations(query);
        if (cancelled) return;
        setLandmarkSearchResults(results);
        setLandmarkSearchOpen(true);
      } catch (_) {
        if (cancelled) return;
        setLandmarkSearchResults([]);
        setLandmarkSearchOpen(false);
      } finally {
        if (!cancelled) {
          setLandmarkSearchLoading(false);
        }
      }
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [landmarkFormOpen, landmarkLocationQuery, searchLandmarkLocations]);

  useEffect(() => {
    if (typeof onLandmarkFocusChange !== "function") return undefined;
    if (!landmarkFormOpen) {
      onLandmarkFocusChange(null);
      return undefined;
    }

    const latitude = Number(landmarkForm?.latitude);
    const longitude = Number(landmarkForm?.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      onLandmarkFocusChange(null);
      return undefined;
    }

    onLandmarkFocusChange({
      latitude,
      longitude,
      lat: latitude,
      lng: longitude,
    });

    return () => {};
  }, [landmarkForm?.latitude, landmarkForm?.longitude, landmarkFormOpen, onLandmarkFocusChange]);

  useEffect(() => {
    if (!landmarkFormOpen || !landmarkDraftValue) return;
    const latitude = Number(landmarkDraftValue?.latitude ?? landmarkDraftValue?.lat);
    const longitude = Number(landmarkDraftValue?.longitude ?? landmarkDraftValue?.lng);
    const radius = Math.max(0, Number(landmarkDraftValue?.radius ?? landmarkDraftValue?.radiusMeters ?? 30));
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

    setLandmarkPlacementActive(true);
    setLandmarkForm((prev) => {
      const same =
        Math.abs(Number(prev?.latitude || 0) - latitude) < 0.000001 &&
        Math.abs(Number(prev?.longitude || 0) - longitude) < 0.000001 &&
        Math.abs(Number(prev?.radius || 0) - radius) < 0.5;
      if (same) return prev;
      landmarkRadiusSyncSourceRef.current = "draft";
      return {
        ...prev,
        latitude,
        longitude,
        radius,
      };
    });
  }, [landmarkDraftValue, landmarkFormOpen]);

  useEffect(() => {
    if (!landmarkFormOpen || typeof onLandmarkDraftChange !== "function") return;
    if (!landmarkDraftValue) return;
    if (landmarkRadiusSyncSourceRef.current === "draft") {
      landmarkRadiusSyncSourceRef.current = "idle";
      return;
    }
    if (landmarkRadiusSyncSourceRef.current !== "user") return;

    const nextRadius = Math.max(0, Number(landmarkForm?.radius ?? 30));
    const currentRadius = Math.max(
      0,
      Number(landmarkDraftValue?.radius ?? landmarkDraftValue?.radiusMeters ?? 30)
    );

    if (Math.abs(nextRadius - currentRadius) < 0.5) return;

    onLandmarkDraftChange((currentDraft) => {
      if (!currentDraft) return currentDraft;
      return {
        ...currentDraft,
        radius: nextRadius,
      };
    });
    landmarkRadiusSyncSourceRef.current = "idle";
  }, [
    landmarkDraftValue,
    landmarkForm?.radius,
    landmarkFormOpen,
    onLandmarkDraftChange,
  ]);
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!landmarkSearchDropdownRef.current?.contains(event.target)) {
        setLandmarkSearchOpen(false);
      }
    };

    if (!landmarkSearchOpen) return undefined;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [landmarkSearchOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOpenLandmarkTab = (event) => {
      openCreateLandmarkForm(event?.detail || null);
    };

    window.addEventListener("gps-open-landmark-tab", handleOpenLandmarkTab);
    return () => window.removeEventListener("gps-open-landmark-tab", handleOpenLandmarkTab);
  }, [openCreateLandmarkForm]);

  const historyDeviceOptions = useMemo(() => {
    const scored = allDevicesList
      .filter(deviceMatchesSidebarStatus)
      .map((device) => {
        const meta = buildHistoryDeviceMatchMeta(device, historyDeviceQuery);
        if (!meta.matches) return null;
        return { device, score: meta.score, label: meta.label };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return String(a.label).localeCompare(String(b.label));
      })
      .map((entry) => entry.device);

    return scored.slice(0, 14);
  }, [allDevicesList, deviceMatchesSidebarStatus, historyDeviceQuery]);

  const selectedHistoryDevice = useMemo(
    () => allDevicesList.find((device) => String(device?._id) === String(historyDeviceId)) || null,
    [allDevicesList, historyDeviceId]
  );

  const notificationItems = useMemo(() => {
    const query = safeLower(searchTerm).trim();
    const eventValues = [];
    const filtered = (Array.isArray(notifications) ? notifications : []).filter((alarm) => {
      const eventName = String(alarm?.alarmType || alarm?.eventType || "");
      if (eventName) eventValues.push(eventName);
      const deviceLabel = safeLower(
        alarm?.deviceName ||
        alarm?.vehicleNumber ||
        alarm?.name ||
        alarm?.imei ||
        ""
      );
      const matchesQuery = !query || deviceLabel.includes(query);
      if (!matchesQuery) return false;
      if (activeStatus === "all") return true;
      const relatedDevice = getSidebarRelatedDevice(alarm);
      return relatedDevice ? deviceMatchesSidebarStatus(relatedDevice) : false;
    });

    return {
      rows: filtered,
      eventOptions: Array.from(new Set(eventValues)).sort((a, b) => a.localeCompare(b)),
    };
  }, [activeStatus, deviceMatchesSidebarStatus, getSidebarRelatedDevice, notifications, searchTerm]);

  useEffect(() => {
    if (!selectedHistoryDevice || historyDeviceDropdownOpen) return;
    setHistoryDeviceQuery(getHistoryDeviceLabel(selectedHistoryDevice));
  }, [selectedHistoryDevice, historyDeviceDropdownOpen]);

  useEffect(() => {
    if (!historySelection?.deviceId) return;
    const nextKey = `${historySelection?.deviceId || ""}:${historySelection?.deviceName || ""}:${historySelection?.requestedAt || ""}`;
    if (lastAppliedHistorySelectionRef.current === nextKey) return;
    lastAppliedHistorySelectionRef.current = nextKey;
    const startParts = getHistoryDateTimeParts(
      historySelection?.startTimestamp || historySelection?.startTime,
      "start"
    );
    const endParts = getHistoryDateTimeParts(
      historySelection?.endTimestamp || historySelection?.endTime,
      "end"
    );
    setActiveTab("History");
    setHistoryDeviceId(historySelection.deviceId);
    setHistoryDeviceQuery(historySelection.deviceName || "");
    setHistoryDeviceDropdownOpen(false);
    if (startParts && endParts) {
      setHistoryFilter("Custom");
      setHistoryFromDate(startParts.date);
      setHistoryToDate(endParts.date);
      setHistoryFromHour(startParts.hour);
      setHistoryFromMinute(startParts.minute);
      setHistoryFromMeridiem(startParts.meridiem);
      setHistoryToHour(endParts.hour);
      setHistoryToMinute(endParts.minute);
      setHistoryToMeridiem(endParts.meridiem);
    } else {
      resetHistoryRangeToToday();
    }
    setHistoryVisible(false);
    setHistoryResults([]);
    setHistoryError("");
    lastAutoPlaybackKeyRef.current = "";
    pendingHistoryPlaybackTargetRef.current = historySelection?.playbackMode === "report"
      ? {
          startTimestamp: historySelection?.startTimestamp || historySelection?.startTime || null,
          endTimestamp: historySelection?.endTimestamp || historySelection?.endTime || null,
        }
      : null;
  }, [
    historySelection?.deviceId,
    historySelection?.deviceName,
    historySelection?.requestedAt,
    historySelection?.startTimestamp,
    historySelection?.startTime,
    historySelection?.endTimestamp,
    historySelection?.endTime,
    historySelection?.playbackMode,
    resetHistoryRangeToToday,
  ]);

  const toggleNotificationEventFilter = useCallback((eventName) => {
    setNotificationEventFilters((prev) =>
      prev.includes(eventName) ? prev.filter((item) => item !== eventName) : [...prev, eventName]
    );
  }, []);

  const filteredNotificationRows = useMemo(() => {
    if (!notificationEventFilters.length) return notificationItems.rows;
    const selected = new Set(notificationEventFilters.map(String));
    return notificationItems.rows.filter((alarm) =>
      selected.has(String(alarm?.alarmType || alarm?.eventType || ""))
    );
  }, [notificationEventFilters, notificationItems.rows]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!historyDeviceDropdownRef.current?.contains(event.target)) {
        setHistoryDeviceDropdownOpen(false);
      }
    };

    if (!historyDeviceDropdownOpen) return undefined;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [historyDeviceDropdownOpen]);

  useEffect(() => {
    if (historyFilter !== "Today") return;
    const today = toYMD(new Date());
    setHistoryFromDate(today);
    setHistoryToDate(today);
    setHistoryFromHour("12");
    setHistoryFromMinute("00");
    setHistoryFromMeridiem("AM");
    setHistoryToHour("11");
    setHistoryToMinute("59");
    setHistoryToMeridiem("PM");
  }, [historyFilter]);

  useEffect(() => {
    if (!historyVisible || !historyResults.length || !historyDeviceId) return;

    const pendingRows = historyResults.filter(
      (row) =>
        (!row.startAddressResolved && (row.startAddress === "NA" || !row.startAddress) && row.startCoords) ||
        (!row.endAddressResolved && (row.endAddress === "NA" || !row.endAddress) && row.endCoords)
    );

    if (!pendingRows.length) return;

    let cancelled = false;
    const baseUrl =
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_URL ||
      "https://qylmox.in/api";

    const resolveAddress = async (coords) => {
      if (!coords?.lat || !coords?.lng) return "NA";
      try {
        const response = await fetch(
          `${baseUrl}/geocode/reverse?lat=${coords.lat}&lon=${coords.lng}`,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) return "NA";
        const data = await response.json();
        return (
          data?.address ||
          data?.address?.full ||
          data?.address?.short ||
          "NA"
        );
      } catch {
        return "NA";
      }
    };

    (async () => {
      const resolvedRows = await Promise.all(
        pendingRows.map(async (row) => {
          const [nextStart, nextEnd] = await Promise.all([
            row.startAddress === "NA" || !row.startAddress
              ? resolveAddress(row.startCoords)
              : Promise.resolve(row.startAddress),
            row.endAddress === "NA" || !row.endAddress
              ? resolveAddress(row.endCoords)
              : Promise.resolve(row.endAddress),
          ]);

          return {
            id: row.id,
            startAddress: nextStart,
            endAddress: nextEnd,
          };
        })
      );

      if (cancelled) return;

      const resolvedById = new Map(
        resolvedRows.map((row) => [row.id, row])
      );

      setHistoryResults((prev) =>
        prev.map((item) => {
          const resolved = resolvedById.get(item.id);
          if (!resolved) return item;
          return {
            ...item,
            startAddress: resolved.startAddress || item.startAddress,
            endAddress: resolved.endAddress || item.endAddress,
            startAddressResolved: true,
            endAddressResolved: true,
          };
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [historyVisible, historyResults, historyDeviceId]);

  useEffect(() => {
    if (groups.length && Object.keys(openGroups || {}).length <= 1) {
      const next = { ungrouped: true };
      for (let i = 0; i < Math.min(2, groups.length); i++) next[groups[i]._id] = true;
      setOpenGroups(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const toggleGroup = (key) => setOpenGroups((p) => ({ ...p, [key]: !p[key] }));

  const objectRows = useMemo(() => {
    const rows = [];
    const forceExpandFilteredGroups =
      activeStatus !== "all" || Boolean(safeLower(searchTerm).trim());
    Object.entries(groupedDevices).forEach(([key, group]) => {
      const list = filterList(group.devices);
      if (!list.length) return;
      rows.push({
        type: "group",
        key: `group-${key}`,
        groupKey: key,
        groupName: group.name,
        devices: list,
      });
      if (forceExpandFilteredGroups || openGroups[key]) {
        list.forEach((device) => {
          rows.push({
            type: "device",
            key: `device-${device._id}`,
            device,
          });
        });
      }
    });
    return rows;
  }, [activeStatus, activeTab, groupedDevices, filterList, openGroups, searchTerm]);

  const closeHistoryExportMenu = () => setHistoryExportAnchor(null);

  const openDeviceHistoryPanel = useCallback((device) => {
    if (!device) return;
    const label = getHistoryDeviceLabel(device);
    setActiveTab("History");
    setHistoryDeviceId(String(device?._id || ""));
    setHistoryDeviceQuery(label);
    setHistoryDeviceDropdownOpen(false);
    resetHistoryRangeToToday();
    setHistoryVisible(false);
    setHistoryResults([]);
    setHistoryError("");
    lastAutoPlaybackKeyRef.current = "";
  }, [resetHistoryRangeToToday]);

  const fetchHistoryReport = useCallback(async () => {
    let resolvedHistoryDeviceId = String(historyDeviceId || "");
    if (!resolvedHistoryDeviceId && historyDeviceQuery?.trim()) {
      const matchedDevice = allDevicesList
        .map((device) => ({
          device,
          meta: buildHistoryDeviceMatchMeta(device, historyDeviceQuery),
        }))
        .filter((entry) => entry.meta.matches)
        .sort((left, right) => {
          if (left.meta.score !== right.meta.score) return left.meta.score - right.meta.score;
          return String(left.meta.label || "").localeCompare(String(right.meta.label || ""));
        })[0]?.device;
      if (matchedDevice?._id) {
        resolvedHistoryDeviceId = String(matchedDevice._id);
        setHistoryDeviceId(resolvedHistoryDeviceId);
        setHistoryDeviceQuery(
          getHistoryDeviceLabel(matchedDevice) || historyDeviceQuery
        );
      }
    }

    if (!resolvedHistoryDeviceId) {
      setHistoryError("Please select a device.");
      setHistoryResults([]);
      setHistoryVisible(false);
      return;
    }

    const isTodayFilter = String(historyFilter || "").toLowerCase() === "today";
    const effectiveToday = toYMD(new Date());
    const effectiveFromDate = isTodayFilter ? effectiveToday : historyFromDate;
    const effectiveToDate = isTodayFilter ? effectiveToday : historyToDate;
    const effectiveFromHour = isTodayFilter ? "12" : historyFromHour;
    const effectiveFromMinute = isTodayFilter ? "00" : historyFromMinute;
    const effectiveFromMeridiem = isTodayFilter ? "AM" : historyFromMeridiem;
    const effectiveToHour = isTodayFilter ? "11" : historyToHour;
    const effectiveToMinute = isTodayFilter ? "59" : historyToMinute;
    const effectiveToMeridiem = isTodayFilter ? "PM" : historyToMeridiem;

    if (isTodayFilter) {
      if (
        historyFromDate !== effectiveFromDate ||
        historyToDate !== effectiveToDate ||
        historyFromHour !== effectiveFromHour ||
        historyFromMinute !== effectiveFromMinute ||
        historyFromMeridiem !== effectiveFromMeridiem ||
        historyToHour !== effectiveToHour ||
        historyToMinute !== effectiveToMinute ||
        historyToMeridiem !== effectiveToMeridiem
      ) {
        setHistoryFromDate(effectiveFromDate);
        setHistoryToDate(effectiveToDate);
        setHistoryFromHour(effectiveFromHour);
        setHistoryFromMinute(effectiveFromMinute);
        setHistoryFromMeridiem(effectiveFromMeridiem);
        setHistoryToHour(effectiveToHour);
        setHistoryToMinute(effectiveToMinute);
        setHistoryToMeridiem(effectiveToMeridiem);
      }
    }

    const startDt = buildDateTimeFromParts(
      effectiveFromDate,
      effectiveFromHour,
      effectiveFromMinute,
      effectiveFromMeridiem
    );
    const endDt = buildDateTimeFromParts(
      effectiveToDate,
      effectiveToHour,
      effectiveToMinute,
      effectiveToMeridiem
    );
    if (!startDt || !endDt || startDt > endDt) {
      setHistoryError("Please choose a valid date range.");
      setHistoryResults([]);
      setHistoryVisible(false);
      return;
    }

    setHistoryLoading(true);
    setHistoryError("");
    try {
      const token = localStorage.getItem("token");
      const apiBase =
        process.env.REACT_APP_BACKEND_URL ||
        process.env.REACT_APP_API_URL ||
        "https://qylmox.in/api";
      const params = new URLSearchParams({
        startDate: effectiveFromDate,
        endDate: effectiveToDate,
        // The sidebar needs a quick trip/day list.  Full report generation
        // performs ride segmentation and reverse geocoding, and must not run
        // again while a playback frame updates the map.
        mode: "summary",
      });
      const resp = await fetch(`${apiBase}/history/${resolvedHistoryDeviceId}/report?${params.toString()}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "x-auth-token": token || "",
          "Content-Type": "application/json",
        },
      });
      if (!resp.ok) throw new Error(`History API failed (${resp.status})`);
      const data = await resp.json();
      const rides = Array.isArray(data?.rides) ? data.rides : [];
      const dailyData = Array.isArray(data?.dailyData) ? data.dailyData : [];
      let normalized = (rides.length ? rides : dailyData).map((item, index) => {
        const date = item?.date || effectiveFromDate;
        const startTime = item?.startTime || `${date}T00:00:00`;
        const endTime = item?.endTime || `${date}T00:00:00`;
        const distanceValue = Number(item?.distanceRawKm ?? item?.distance ?? 0);
        const durationValue = Number(item?.movingTime ?? item?.duration ?? 0);
        return {
          id: String(item?.id || item?._id || `${date}-${index}`),
          startTimeRaw: startTime,
          endTimeRaw: endTime,
          startLabel: formatHistoryDateLabel(startTime),
          endLabel: formatHistoryDateLabel(endTime),
          startAddress: extractHistoryAddress(item, "start"),
          endAddress: extractHistoryAddress(item, "end"),
          startCoords: extractHistoryCoords(item, "start"),
          endCoords: extractHistoryCoords(item, "end"),
          startAddressResolved: extractHistoryAddress(item, "start") !== "NA",
          endAddressResolved: extractHistoryAddress(item, "end") !== "NA",
          durationText: formatDurationMins(durationValue),
          distanceText: formatHistoryDistance(distanceValue),
          isRide: distanceValue > 0.05,
        };
      }).filter((item) => {
        const tripStart = new Date(item.startTimeRaw).getTime();
        const tripEnd = new Date(item.endTimeRaw).getTime();
        const rangeStart = startDt.getTime();
        const rangeEnd = endDt.getTime();
        if (!Number.isFinite(tripStart) || !Number.isFinite(tripEnd)) return true;
        return tripEnd >= rangeStart && tripStart <= rangeEnd;
      });

      const shouldUsePlaybackFallback =
        params.get("mode") !== "summary" &&
        !rides.length &&
        (
          normalized.length <= 1 ||
          normalized.every(
            (item) =>
              !item.isRide &&
              (!item.startAddressResolved || item.startAddress === "NA") &&
              (!item.endAddressResolved || item.endAddress === "NA") &&
              safeLower(item.distanceText).startsWith("0 ")
          )
        );

      if (shouldUsePlaybackFallback) {
        const fallbackPlaybackResp = await fetch(
          `${apiBase}/history/${resolvedHistoryDeviceId}/playback?startDate=${encodeURIComponent(effectiveFromDate)}&endDate=${encodeURIComponent(effectiveToDate)}&startTs=${encodeURIComponent(startDt.toISOString())}&endTs=${encodeURIComponent(endDt.toISOString())}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "x-auth-token": token || "",
              "Content-Type": "application/json",
            },
          }
        );
        if (fallbackPlaybackResp.ok) {
          const fallbackPlayback = await fallbackPlaybackResp.json();
          const fallbackPoints = Array.isArray(fallbackPlayback?.points) ? fallbackPlayback.points : [];
          const playbackRows = buildHistoryRowsFromPlaybackPoints(fallbackPoints);
          if (playbackRows.length) {
            normalized = playbackRows;
          }
        }
      }

      setHistoryResults(normalized);
      setHistoryVisible(true);
      if (!normalized.length) setHistoryError("No history found for selected range.");

      // Auto-open playback when history is fetched and we have at least one ride.
      // Uses /history/:deviceId/playback so backend stays fast (no geocoding).
      if (normalized.length && typeof onOpenPlayback === "function") {
        const pendingPlaybackTarget = pendingHistoryPlaybackTargetRef.current;
        const playbackStartRaw = pendingPlaybackTarget?.startTimestamp || normalized[0]?.startTimeRaw;
        const playbackEndRaw = pendingPlaybackTarget?.endTimestamp || normalized[0]?.endTimeRaw;
        const playbackStartDate = toYMD(playbackStartRaw) || effectiveFromDate;
        const playbackEndDate = toYMD(playbackEndRaw) || effectiveToDate;
        const playbackStartTs = new Date(playbackStartRaw || normalized[0]?.startTimeRaw).toISOString();
        const playbackEndTs = new Date(playbackEndRaw || normalized[0]?.endTimeRaw).toISOString();
        const key = `${resolvedHistoryDeviceId}:${playbackStartTs}:${playbackEndTs}`;
        if (lastAutoPlaybackKeyRef.current !== key) {
          lastAutoPlaybackKeyRef.current = key;
          const first = normalized[0];
          const pbResp = await fetch(
            `${apiBase}/history/${resolvedHistoryDeviceId}/playback?startDate=${encodeURIComponent(playbackStartDate)}&endDate=${encodeURIComponent(playbackEndDate)}&startTs=${encodeURIComponent(playbackStartTs)}&endTs=${encodeURIComponent(playbackEndTs)}`,
            {
              headers: {
                Authorization: token ? `Bearer ${token}` : "",
                "x-auth-token": token || "",
                "Content-Type": "application/json",
              },
            }
          );
          if (pbResp.ok) {
            const pb = await pbResp.json();
            const points = Array.isArray(pb?.points) ? pb.points : [];
            if (points.length) {
              onOpenPlayback({
                id: `history_${resolvedHistoryDeviceId}_${first.id}`,
                deviceId: resolvedHistoryDeviceId,
                date: first?.startLabel || effectiveFromDate,
                route: points,
                distance: Number(pb?.stats?.totalDistanceRawKm ?? 0),
                durationSeconds: Number(pb?.stats?.movingTime ?? 0) + Number(pb?.stats?.idleTime ?? 0),
                durationLabel: undefined,
                startTime: first.startTimeRaw,
                endTime: first.endTimeRaw,
              });
            }
          }
        }
      }
      pendingHistoryPlaybackTargetRef.current = null;
    } catch (error) {
      pendingHistoryPlaybackTargetRef.current = null;
      setHistoryError(error?.message || "Failed to load history.");
      setHistoryResults([]);
      setHistoryVisible(false);
    } finally {
      setHistoryLoading(false);
    }
  }, [
    allDevicesList,
    historyDeviceId,
    historyDeviceQuery,
    historyFilter,
    historyFromDate,
    historyFromHour,
    historyFromMinute,
    historyFromMeridiem,
    historyToDate,
    historyToHour,
    historyToMinute,
    historyToMeridiem,
    onOpenPlayback,
  ]);

  useEffect(() => {
    if (!historySelection?.deviceId) return;
    if (String(historyDeviceId || "") !== String(historySelection.deviceId || "")) return;
    const requestKey = [
      historySelection.deviceId,
      historySelection.requestedAt || "",
      historySelection.startTimestamp || historySelection.startTime || "",
      historySelection.endTimestamp || historySelection.endTime || "",
    ].join(":");
    if (lastFetchedHistorySelectionRef.current === requestKey) return;
    lastFetchedHistorySelectionRef.current = requestKey;
    fetchHistoryReport();
    // A new callback is created when live-map props change during playback.
    // This request is keyed to the user's explicit history selection, not a
    // 30 FPS playback render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyDeviceId, historySelection?.deviceId, historySelection?.requestedAt, historySelection?.startTimestamp, historySelection?.startTime, historySelection?.endTimestamp, historySelection?.endTime]);

  const exportHistoryData = useCallback((type) => {
    if (!historyResults.length) return;
    const payload = historyResults.map((item) => ({
      start_time: item.startLabel,
      start_address: item.startAddress,
      end_time: item.endLabel,
      end_address: item.endAddress,
      duration: item.durationText,
      distance: item.distanceText,
      trip_type: item.isRide ? "ride" : "stop",
    }));
    let blob;
    let filename;
    if (type === "json") {
      blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      filename = "history-export.json";
    } else {
      const header = "Start Time,Start Address,End Time,End Address,Duration,Distance,Trip Type\n";
      const rows = payload
        .map((row) => [row.start_time, row.start_address, row.end_time, row.end_address, row.duration, row.distance, row.trip_type]
          .map((value) => `"${String(value || "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
      filename = "history-export.csv";
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    closeHistoryExportMenu();
  }, [historyResults]);

  const handleImportHistoryFile = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      let rows = [];
      if (file.name.toLowerCase().endsWith(".json")) {
        const parsed = JSON.parse(text);
        rows = Array.isArray(parsed) ? parsed : [];
      } else {
        const lines = text.split(/\r?\n/).filter(Boolean);
        rows = lines.slice(1).map((line, index) => {
          const parts = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map((part) => part.replace(/^"|"$/g, "").replace(/""/g, '"')) || [];
          return {
            id: `import-${index}`,
            startLabel: parts[0] || "",
            startAddress: parts[1] || "NA",
            endLabel: parts[2] || "",
            endAddress: parts[3] || "NA",
            durationText: parts[4] || "00:00 min",
            distanceText: parts[5] || "0 Kms",
            isRide: String(parts[6] || "").toLowerCase() === "ride",
          };
        });
      }
      const normalized = rows.map((item, index) => ({
        id: item.id || `import-${index}`,
        startLabel: item.startLabel || item.start_time || "",
        startAddress: item.startAddress || item.start_address || "NA",
        endLabel: item.endLabel || item.end_time || "",
        endAddress: item.endAddress || item.end_address || "NA",
        durationText: item.durationText || item.duration || "00:00 min",
        distanceText: item.distanceText || item.distance || "0 Kms",
        isRide: item.isRide ?? String(item.trip_type || "").toLowerCase() === "ride",
      }));
      setHistoryResults(normalized);
      setHistoryVisible(true);
      setHistoryError("");
    } catch (error) {
      setHistoryError("Failed to import history file.");
    } finally {
      event.target.value = "";
      closeHistoryExportMenu();
    }
  }, []);

  const openMoreMenu = useCallback((event, device) => {
    event.stopPropagation();
    const nativeEvent = event.nativeEvent || event;
    const clientX = Number(nativeEvent?.clientX ?? 0);
    const clientY = Number(nativeEvent?.clientY ?? 0);
    setMenuAnchor(event.currentTarget);
    setMenuPosition({
      top: Math.round(clientY + 6),
      left: Math.round(clientX - 8),
    });
    setMenuDevice(device);
    setIsMenuInteractionLocked(true);
  }, []);

  const closeMoreMenu = useCallback(() => {
    setMenuAnchor(null);
    setMenuPosition(null);
    setMenuDevice(null);
    setIsMenuInteractionLocked(false);
  }, []);
  const openNotificationMenu = (e, alarm) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setNotificationMenuAnchor(e.currentTarget);
    setNotificationMenuPosition({
      top: Math.round(rect.top + rect.height / 2),
      left: Math.round(rect.left - 8),
    });
    setMenuNotification(alarm);
  };
  const closeNotificationMenu = () => {
    setNotificationMenuAnchor(null);
    setNotificationMenuPosition(null);
    setMenuNotification(null);
  };
  const openRemarkDialog = () => {
    setRemarkAlarm(menuNotification);
    setRemarkText(menuNotification?.notes || "");
    setRemarkDialogOpen(true);
    closeNotificationMenu();
  };
  const handleSaveRemark = async () => {
    if (!remarkAlarm?._id && !remarkAlarm?.alarmId) return;
    const alarmId = remarkAlarm?._id || remarkAlarm?.alarmId;
    try {
      await updateAlarmRemark({ alarmId, notes: remarkText }).unwrap();
      setRemarkDialogOpen(false);
      setRemarkAlarm((prev) => prev ? { ...prev, notes: remarkText } : prev);
    } catch {
      // keep dialog open on failure
    }
  };
  const handleRefreshClick = () => {
    if (activeTab === "Notifications") {
      onRefreshNotifications?.();
      return;
    }
    setTick((t) => t + 1);
  };
  const resolveShareHours = useCallback(() => {
    if (sharePreset === "custom") {
      const parsed = Number(shareCustomHours);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return parsed;
    }
    const matched = SHARE_PRESETS.find((item) => item.value === sharePreset);
    return matched?.hours || 1;
  }, [shareCustomHours, sharePreset]);

  const generateShareLink = useCallback(async (device, requestedHours) => {
    if (!device?._id) return;
    setShareLoading(true);
    try {
      const response = await api.get(`/devices/${device._id}/share-link`, {
        params: { hours: requestedHours },
      });
      const token = response?.data?.token;
      const nextLink = `${window.location.origin}/live-tracking?t=${encodeURIComponent(token || "")}`;
      setShareLink(nextLink);
      setShareExpiresAt(response?.data?.expiresAt || "");
    } catch (error) {
      setShareLink("");
      setShareExpiresAt("");
      showStatus("error", error?.response?.data?.msg || "Failed to generate share link.");
    } finally {
      setShareLoading(false);
    }
  }, [showStatus]);

  const openShareDialog = useCallback((device) => {
    setShareDevice(device || null);
    setSharePreset("1");
    setShareCustomHours("1");
    setShareLink("");
    setShareExpiresAt("");
    setShareDialogOpen(true);
    if (device?._id) {
      generateShareLink(device, 1);
    }
  }, [generateShareLink]);

  const closeShareDialog = useCallback(() => {
    setShareDialogOpen(false);
    setShareDevice(null);
    setShareLink("");
    setShareExpiresAt("");
    setShareLoading(false);
  }, []);

  const handleRegenerateShareLink = useCallback(() => {
    const hours = resolveShareHours();
    if (!shareDevice?._id || !hours) {
      showStatus("warning", "Please enter valid share duration.");
      return;
    }
    generateShareLink(shareDevice, hours);
  }, [generateShareLink, resolveShareHours, shareDevice, showStatus]);

  const handleCopyShareLink = useCallback(async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      showStatus("success", "Share link copied.");
    } catch {
      window.prompt("Copy this link:", shareLink);
    }
  }, [shareLink, showStatus]);

  const handleOpenShareLink = useCallback(() => {
    if (!shareLink) return;
    window.open(shareLink, "_blank", "noopener,noreferrer");
  }, [shareLink]);

  const handleWhatsAppShare = useCallback(() => {
    if (!shareLink) return;
    const vehicleName = shareDevice?.name || shareDevice?.vehicleNumber || shareDevice?.imei || "Vehicle";
    const text = `Track ${vehicleName} live: ${shareLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  }, [shareDevice, shareLink]);

  const isAllSelected =
    visibleFilteredDevices.length > 0 &&
    visibleFilteredDevices.every((device) => selectedDevices.includes(device._id));

  useEffect(() => {
    finishDeviceSidebarProfile({
      props: {
        allDevicesCount: allDevicesList.length,
        selectedDevicesCount: selectedDevices.length,
        searchQuery: searchTerm,
        status: activeStatus,
        activeTab,
      },
      state: {},
      deviceCount: allDevicesList.length,
    });
  });
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectAllLoaded(false);
      setSelectedDevices?.([]);
    } else {
      setSelectAllLoaded(true);
      setSelectedDevices?.(visibleFilteredDevices.map((device) => device._id));
    }
  };
  const toggleSelectDevice = useCallback((id) => {
    setSelectAllLoaded(false);
    setSelectedDevices?.((prevSelected = []) => (
      prevSelected.includes(id)
        ? prevSelected.filter((x) => x !== id)
        : [...prevSelected, id]
    ));
  }, [setSelectedDevices]);

  // ── DeviceRow ──────────────────────────────────────────────────────────────
  const DeviceRow = useMemo(() => React.memo(function DeviceRow({
    device,
    liveInput,
    isSelected,
    isFocused,
  }) {
    const deviceKey = String(device?._id || device?.imei || "");
    const socketData = liveInput || {};
    const apiLive = device?.deviceLive || device?.liveSnapshot || {};

    const socketTs = toTimestampMs(socketData?.packetReceivedAt ?? socketData?.lastSocketUpdate ?? socketData?.packetTime ?? socketData?.serverReceivedAt ?? socketData?.receivedAt ?? socketData?.timestamp ?? socketData?.serverTime);
    const dbTs = apiLive?.timestamp ? toTimestampMs(apiLive.timestamp) : device?.liveData?.timestamp ? toTimestampMs(device.liveData.timestamp) : toTimestampMs(device?.lastDataReceivedAt) || 0;
    const hasSocketPayload = socketData && Object.keys(socketData).length > 0;
    const socketAgeMs = socketTs > 0 ? Date.now() - socketTs : Infinity;
    const socketFresh = Number.isFinite(socketAgeMs) && socketAgeMs <= 30000;
    const useSocketData = hasSocketPayload && (socketFresh || (socketTs > 0 && socketTs + 20000 >= dbTs));
    const data = useSocketData ? { ...device?.liveData, ...apiLive, ...socketData } : { ...device?.liveData, ...apiLive };
    // Match MapContainer/popup exactly: speed always comes from the latest
    // socket snapshot when one exists. The freshness gate is useful for DB
    // field merging, but must not make the sidebar fall back to an old speed.
    const snapshot = getResolvedLiveSnapshot(device, socketData);

    const status = getBackendSidebarStatus(device, liveInput);

    const lat = Number(snapshot.latitude ?? data?.latitude ?? data?.lat ?? device?.currentLocation?.latitude ?? device?.liveData?.latitude);
    const lng = Number(snapshot.longitude ?? data?.longitude ?? data?.lng ?? device?.currentLocation?.longitude ?? device?.liveData?.longitude);
    const rawSpeed = Number(snapshot.speed ?? data?.speed ?? 0);
    const accSignal = parseAccBool(data?.accStatus ?? data?.acc ?? data?.ignition);
    const currentSpeed = rawSpeed;
    const packetTs = toTimestampMs(
      data?.packetReceivedAt ??
      data?.lastSocketUpdate ??
      data?.packetTime ??
      data?.timestamp ??
      data?.serverTime ??
      data?.lastDataReceivedAt ??
      data?.lastUpdate
    ) || 0;

    const prevTracking = trackingStateByDeviceRef.current.get(deviceKey) || null;
    const tracking = resolveTrackingState({
      prev: prevTracking,
      speed: currentSpeed,
      accStatus: snapshot.accStatus ?? data?.accStatus ?? data?.acc ?? data?.ignition ?? null,
      timestampMs: packetTs,
      latitude: Number.isFinite(lat) ? lat : null,
      longitude: Number.isFinite(lng) ? lng : null,
      heading: data?.heading ?? data?.course ?? data?.bearing ?? 0,
      vehicleStatus: status,
      movementState: data?.movementState ?? data?.movement_state ?? null,
      direction: data?.direction ?? null,
      turnType: data?.turnType ?? data?.turn_type ?? null,
    });
    if (deviceKey) trackingStateByDeviceRef.current.set(deviceKey, tracking);
    const resolvedStatus = status;
    // A state must not erase the latest reported speed. The canonical status
    // still controls the row/icon colour independently.
    const displaySpeed = rawSpeed;

    const ignOn = accSignal === null
      ? resolvedStatus === "running" || resolvedStatus === "idle"
      : accSignal === true;

    const freshestTs = Math.max(socketTs || 0, dbTs || 0, packetTs || 0);
    const hasNeverReportedData = resolvedStatus === "no_data" || (freshestTs <= 0 && resolvedStatus !== "expired");
    const lastDate = hasNeverReportedData ? "" : (freshestTs ? formatDate(freshestTs) : formatDate(device?.lastDataReceivedAt));

    const iconFamilyKey = device?.vehicleType ?? device?.deviceIcon ?? device?.vehicleIcon ?? device?.icon ?? "car";
    const cloudinaryIcon = resolveSidebarCloudinaryIcon(iconFamilyKey, resolvedStatus);
    const defaultIcon = typeof getVehicleIcon === "function" ? getVehicleIcon(iconFamilyKey) : null;
    const iconSrc = cloudinaryIcon || defaultIcon;

    // GPS signal: treat as "on" if we have valid coords
    const gpsOn = Number.isFinite(lat) && lat !== 0 && Number.isFinite(lng) && lng !== 0;
    // PWR: keep green in stopped unless an explicit external power-cut signal says otherwise.
    const explicitPowerState = parsePowerBool(
      data?.power ?? data?.externalPower ?? data?.mainPower ?? data?.powerStatus
    );
    const batteryPowerState =
      data?.battery !== undefined && data?.battery !== null && data?.battery !== ""
        ? Number(data.battery) > 0
        : null;
    const pwrOn =
      explicitPowerState !== null
        ? explicitPowerState
        : batteryPowerState !== null
          ? batteryPowerState
          : resolvedStatus === "stopped"
            ? true
            : ignOn;
    const telemetryIcons = getTelemetryIconState({
      status: resolvedStatus,
      ignOn,
      gpsOn,
      pwrOn,
    });

    // Status dot color
    const dotColor = {
      running: "#16a34a", idle: "#ca8a04", stopped: "#dc2626",
      inactive: "#2563eb", no_data: "#6b7280", expired: "#7c3aed",
    }[resolvedStatus] || "#6b7280";

    return (
      <Box
  onClick={(e) => {
    if (e.defaultPrevented) return;
    if (e.target instanceof Element && e.target.closest('[data-device-row-interactive="true"]')) return;
    // Row clicks are handled by MapTracker as an explicit single-vehicle map
    // view. Checkbox clicks above keep their separate multi-select behavior.
    if (onFocusDevice) onFocusDevice(device);
    else onDeviceClick?.(device);
  }}
        sx={{
          display: "grid",
          gridTemplateColumns: desktopRowTemplate,
          alignItems: "center",
          gap: compactDesktop ? "2px" : "4px",
          px: compactDesktop ? 1.1 : 1.35,
          py: compactDesktop ? "7px" : "9px",
          borderBottom: "1px solid #e9edf3",
          cursor: "pointer",
          background: isSelected ? "#eef5ff" : "#ffffff",
          "&:hover": { background: isSelected ? "#e9f1ff" : "#f8fafc" },
          transition: "background 0.12s",
        }}
      >
        {/* Checkbox 1 — select for map */}
        <Checkbox
  data-device-row-interactive="true"
  size="small"
  checked={isSelected}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); toggleSelectDevice(device._id); }}
          sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
        />

        {/* Checkbox 2 — focused device selector */}
        <Checkbox
  data-device-row-interactive="true"
  size="small"
  checked={isFocused}
  onMouseDown={(e) => e.stopPropagation()}
  onClick={(e) => { e.stopPropagation(); onToggleVisibility?.(device); }}
          sx={{ p: 0, color: "text.disabled", "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
        />

        {/* Vehicle icon + name + date */}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: compactDesktop ? "5px" : "7px", minWidth: 0 }}>
            <Box sx={{ width: compactDesktop ? 24 : 30, height: compactDesktop ? 18 : 22, flexShrink: 0 }}>
              <img
                src={iconSrc || ""}
                alt=""
                onError={(e) => {
                  if (e.currentTarget.dataset.fallbackApplied === "1") return;
                  if (defaultIcon && e.currentTarget.src !== defaultIcon) {
                    e.currentTarget.dataset.fallbackApplied = "1";
                    e.currentTarget.src = defaultIcon;
                    return;
                  }
                  e.currentTarget.dataset.fallbackApplied = "1";
                }}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Typography sx={{ fontSize: compactDesktop ? "12px" : "13px", fontWeight: 700, color: "#253142", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              {device?.vehicleNumber || device?.name || "Unnamed"}
            </Typography>
          </Box>

          {lastDate ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "3px", mt: "1px" }}>
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
              <Typography sx={{ fontSize: compactDesktop ? "7px" : "8px", fontWeight: 600, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
                {lastDate}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 14 }} />
          )}
        </Box>

        {/* Speed */}
        <Box sx={{ textAlign: "center", visibility: telemetryIcons.hidden ? "hidden" : "visible" }}>
          <Typography sx={{ fontSize: compactDesktop ? "12px" : "14px", fontWeight: 500, color: "#1f2937", lineHeight: 1.1 }}>
            {Math.round(displaySpeed)}
          </Typography>
          <Typography sx={{ fontSize: compactDesktop ? "7px" : "8px", color: "#6b7280", lineHeight: 1 }}>kmph</Typography>
        </Box>

        {/* IGN */}
        <IconCell on={telemetryIcons.ignOn} label="IGN" hidden={telemetryIcons.hidden} compact={compactDesktop} />

        {/* GPS */}
        <IconCell on={telemetryIcons.gpsOn} label="GPS" hidden={telemetryIcons.hidden} compact={compactDesktop} />

        {/* PWR */}
        <IconCell on={telemetryIcons.pwrOn} label="PWR" hidden={telemetryIcons.hidden} compact={compactDesktop} />

        {/* More menu */}
       <IconButton
  data-device-row-interactive="true"
  size="small"
  aria-label="Device actions"
  disableRipple
  disableFocusRipple
  disableTouchRipple
  onMouseDown={(e) => e.stopPropagation()}
  onPointerDown={(e) => e.stopPropagation()}
  onClick={(e) => openMoreMenu(e, device)}
  sx={{
    width: compactDesktop ? 30 : 34,
    height: compactDesktop ? 30 : 34,
    ml: compactDesktop ? 0.5 : 0.75,
    p: 0,
    color: "#4b5563",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
    justifySelf: "center",
    borderRadius: "8px",
    zIndex: 2,
    pointerEvents: "auto",
    backgroundColor: "transparent",
    transition: "color 0.12s ease",
    "&:hover": {
      backgroundColor: "transparent",
      color: "#111827",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "transparent",
    },
  }}
>
  <MoreVertIcon sx={{ fontSize: compactDesktop ? 17 : 19, pointerEvents: "none" }} />
  <ExpandMoreIcon sx={{ fontSize: compactDesktop ? 15 : 17, pointerEvents: "none" }} />
</IconButton>
      </Box>
    );
  }, (prevProps, nextProps) => (
    prevProps.device === nextProps.device &&
    prevProps.liveVersion === nextProps.liveVersion &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isFocused === nextProps.isFocused
  )), [
    compactDesktop,
    desktopRowTemplate,
    getVehicleIcon,
    onDeviceClick,
    onFocusDevice,
    onToggleVisibility,
    openMoreMenu,
    setSelectedDevices,
    toggleSelectDevice,
  ]);

  const renderObjectRow = useCallback(({ index, style }) => {
    const row = objectRows[index];
    if (!row) {
      return (
        <Box style={style} sx={{ display: "grid", placeItems: "center", color: "text.secondary", fontSize: "12px" }}>
          {pagedObjectsLoading ? "Loading devices..." : ""}
        </Box>
      );
    }

    if (row.type === "group") {
      const list = row.devices;
      const isOpen = Boolean(openGroups[row.groupKey]);
      const groupSelected = list.every((device) => selectedDevices.includes(device._id));

      return (
        <Box style={style} key={row.key}>
          <Box
            onClick={() => toggleGroup(row.groupKey)}
            sx={{
              height: "100%",
              display: "grid",
              gridTemplateColumns: desktopRowTemplate,
              alignItems: "center",
              gap: compactDesktop ? "2px" : "4px",
              px: compactDesktop ? 1.1 : 1.35,
              py: compactDesktop ? "6px" : "7px",
              background: "#f7f9fc",
              borderBottom: "1px solid #e5e7eb",
              cursor: "pointer",
              "&:hover": { background: "#eef2f7" },
            }}
          >
            <Checkbox
              size="small"
              checked={groupSelected}
              indeterminate={list.some((device) => selectedDevices.includes(device._id)) && !groupSelected}
              onClick={(event) => {
                event.stopPropagation();
                if (groupSelected) {
                  setSelectAllLoaded(false);
                  setSelectedDevices?.(selectedDevices.filter((id) => !list.find((device) => device._id === id)));
                } else {
                  const nextSelection = [...new Set([...selectedDevices, ...list.map((device) => device._id)])];
                  const nextIds = new Set(nextSelection.map((id) => String(id)));
                  const selectsAllLoaded =
                    visibleFilteredDevices.length > 0 &&
                    visibleFilteredDevices.every((device) => nextIds.has(String(device._id)));
                  setSelectAllLoaded(selectsAllLoaded);
                  setSelectedDevices?.(nextSelection);
                }
              }}
              sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: compactDesktop ? 14 : 16 } }}
            />
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "text.secondary" }} /> : <ChevronRightIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "text.secondary" }} />}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: compactDesktop ? "5px" : "7px", minWidth: 0 }}>
              <FolderOutlinedIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "text.secondary" }} />
              <Typography sx={{ fontSize: compactDesktop ? "12px" : "13px", fontWeight: 800, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.groupName}</Typography>
            </Box>
            <Chip
              label={list.length}
              size="small"
              sx={{ height: 18, fontSize: "10px", fontWeight: 700, background: "#334155", color: "#fff", borderRadius: "10px" }}
            />
            <Box /><Box /><Box />
            <IconButton
              size="small"
              onClick={(event) => event.stopPropagation()}
              sx={{
                p: 0,
                color: "text.secondary",
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "transparent" },
                "&.Mui-focusVisible": { backgroundColor: "transparent" },
              }}
            >
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
        </Box>
      );
    }

    // Paged rows are deliberately independent from `allDevicesList`: the
    // compact sidebar endpoint can return a device that is not present in the
    // map's currently cached device array.  Reading that array's metadata here
    // made such rows fall back to an older DB speed while MapContainer used the
    // latest socket packet.  Resolve the row from its own device on every live
    // render, exactly as MapContainer does.
    const liveInput = getLiveStatusInput(row.device);
    const resolvedStatus = getBackendSidebarStatus(row.device, liveInput);
    const liveVersion = getSidebarLiveRenderSignature(liveInput, resolvedStatus);
    const isSelected = selectedDevices.includes(row.device?._id);
    // The second checkbox is an explicit sidebar focus choice. Do not derive
    // it from row clicks or the first (map visibility) checkbox.
    const isFocused = String(focusedDeviceId || "") === String(row.device?._id || "");

    return (
      <Box style={style} key={row.key}>
        <DeviceRow
          device={row.device}
          liveInput={liveInput}
          liveVersion={liveVersion}
          isSelected={isSelected}
          isFocused={isFocused}
        />
      </Box>
    );
  }, [focusedDeviceId, getLiveStatusInput, objectRows, openGroups, pagedObjectsLoading, selectedDevices, setSelectedDevices, tick, visibleFilteredDevices]);

  // ── Column header row ──────────────────────────────────────────────────────
  const ColHeader = (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: desktopRowTemplate,
      alignItems: "center",
      gap: compactDesktop ? "2px" : "4px",
      px: compactDesktop ? 1.1 : 1.5,
      py: "7px",
      background: "#ffffff",
      borderBottom: "1px solid #e5e7eb",
    }}>
      <VisibilityOutlinedIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "#6b7280", justifySelf: "center" }} />
      <FmdGoodOutlinedIcon sx={{ fontSize: compactDesktop ? 14 : 16, color: "#6b7280", justifySelf: "center" }} />
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 600, color: "#6b7280" }}>Object</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 600, color: "#6b7280", textAlign: "center" }}>kmph</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 600, color: "#6b7280", textAlign: "center" }}>IGN</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 600, color: "#6b7280", textAlign: "center" }}>GPS</Typography>
      <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 600, color: "#6b7280", textAlign: "center" }}>PWR</Typography>
      <Box sx={{ textAlign: "center" }}>
        <Typography sx={{ fontSize: compactDesktop ? "10px" : "11px", fontWeight: 600, color: "#6b7280" }}>Obj</Typography>
      </Box>
    </Box>
  );

  // ── Header ─────────────────────────────────────────────────────────────────
  const Header = (
    <Box sx={{ position: "sticky", top: 0, zIndex: 3, background: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
      {/* Offline banner */}
      {!connected && (
        <Box sx={{ px: 1.5, py: 0.75, background: "warning.light", color: "warning.contrastText", display: "flex", alignItems: "center", gap: 1 }}>
          <WifiOffIcon sx={{ fontSize: 14 }} />
          <Typography sx={{ fontSize: "11px", fontWeight: 800 }}>System Offline — Reconnecting...</Typography>
        </Box>
      )}

      {/* Status pills row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.1, pt: 1.1, pb: 0.9, background: "#f7f8fb" }}>
        <IconButton
          size="small"
          onClick={() => scrollStatusPills("left")}
          sx={{ p: "2px", border: "1px solid #e5e7eb", borderRadius: "999px", flexShrink: 0, bgcolor: "#fff", width: 22, height: 22 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box
          ref={statusPillsRef}
          sx={{ display: "flex", alignItems: "center", gap: compactMobile ? 0.75 : "5px", flex: 1, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
        >
          {STATUS_ORDER.map((s) => {
            const cfg = STATUS_PILL_STYLE[s] || STATUS_PILL_STYLE.no_data;
            const isActive = activeStatus === s;
            return (
              <Box
                key={s}
                onClick={() => {
                  setActiveStatus(s);
                  if (s === "all") {
                    setSelectedDevices?.([]);
                  }
                }}
                sx={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  minWidth: compactMobile ? 68 : 76, height: compactMobile ? 56 : 62, px: compactMobile ? 1 : 1.1, py: 0.75,
                  background: cfg.bg,
                  color: cfg.color,
                  borderRadius: "7px",
                  cursor: "pointer",
                  flexShrink: 0,
                  boxShadow: isActive ? "0 6px 14px rgba(15,23,42,0.18)" : "none",
                  transform: isActive ? "translateY(-1px)" : "none",
                  userSelect: "none",
                  transition: "transform 0.14s ease, box-shadow 0.14s ease",
                }}
              >
                <Typography sx={{ fontSize: compactMobile ? "22px" : "30px", fontWeight: 800, lineHeight: 1, color: "inherit" }}>
                  {statusCounts.isReady ? statusCounts[s] || 0 : "—"}
                </Typography>
                <Typography sx={{ mt: 0.45, fontSize: compactMobile ? "9px" : "10px", fontWeight: 700, color: "inherit" }}>
                  {cfg.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <IconButton
          size="small"
          onClick={() => scrollStatusPills("right")}
          sx={{ p: "2px", border: "1px solid #e5e7eb", borderRadius: "999px", flexShrink: 0, bgcolor: "#fff", width: 22, height: 22 }}
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      {progressiveStatusText ? (
        <Box sx={{ px: 1.25, pb: 0.5 }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "text.secondary" }}>
            {progressiveStatusText}
          </Typography>
        </Box>
      ) : null}

      {/* Tabs */}
      <Box sx={{ display: "flex", gap: compactMobile ? 0.5 : 0, px: compactMobile ? 1 : 0.35, py: compactMobile ? 0.75 : 0, borderBottom: "1px solid #e5e7eb", borderColor: "divider", overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        {TABS.map((tab) => (
          <Box
            key={tab}
            onClick={() => setActiveTab(tab)}
            sx={{
              px: compactMobile ? 1.25 : 1.5,
              py: compactMobile ? "8px" : "10px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontSize: compactMobile ? "11px" : "12.5px",
              fontWeight: activeTab === tab ? 700 : 500,
              color: activeTab === tab ? "#111827" : "#6b7280",
              borderBottom: compactMobile ? "none" : "2px solid",
              borderColor: activeTab === tab ? "#111827" : "transparent",
              borderRadius: compactMobile ? "999px" : 0,
              background: compactMobile && activeTab === tab ? "rgba(15,23,42,0.08)" : "transparent",
              transition: "all 0.12s",
            }}
          >
            {tab}
          </Box>
        ))}
      </Box>

      {/* Toolbar: search / actions */}
      {activeTab !== "History" && (
        activeTab === "Notifications" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.85,
              borderBottom: "1px solid #eef2f7",
              background: "#fff",
            }}
          >
            <TextField
              value={searchTerm}
              onChange={(e) => setSearchTerm?.(e.target.value)}
              placeholder="Search Vehicle"
              size="small"
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-root": {
                  minHeight: 40,
                  borderRadius: 0,
                  bgcolor: "#fff",
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  py: "8px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              onClick={(event) => setNotificationEventFilterAnchor(event.currentTarget)}
              sx={{
                minHeight: 40,
                minWidth: compactMobile ? 128 : 162,
                maxWidth: compactMobile ? 144 : 188,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
                cursor: "pointer",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: notificationEventFilters.length ? "text.primary" : "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  pr: 1,
                  minWidth: 0,
                }}
              >
                {notificationEventFilters.length
                  ? `${notificationEventFilters.length} selected`
                  : "Event Filter"}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
            </Box>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={{ p: "4px" }}
                onClick={handleRefreshClick}
                disabled={notificationsRefreshing}
              >
                {notificationsRefreshing ? (
                  <CircularProgress size={16} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={sidebarOpen ? "Collapse" : "Expand"}>
              <IconButton size="small" sx={{ p: "4px" }} onClick={() => setSidebarOpen((s) => !s)}>
                {isMobile ? <CloseIcon sx={{ fontSize: 16 }} /> : sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: compactMobile ? "wrap" : "nowrap",
              px: 1.25,
              py: 0.75,
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: compactMobile ? "13px" : "15px",
                color: "#111827",
                mr: 0.5,
                minWidth: compactMobile ? "100%" : "auto",
                lineHeight: 1.2,
              }}
            >
              {activeTab === "Geofence"
                ? "Geofence"
                : activeTab === "Landmark"
                  ? "Landmark"
                  : "Device"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
              <TextField
                value={searchTerm}
                onChange={(e) => setSearchTerm?.(e.target.value)}
                placeholder={
                  activeTab === "Geofence"
                    ? "Search Geofence"
                    : activeTab === "Landmark"
                      ? "Search Landmark"
                      : "Search Vehicle"
                }
                size="small"
                sx={{
                  flex: 1,
                  minWidth: compactMobile ? "100%" : 0,
                  "& .MuiInputBase-root": {
                    minHeight: 38,
                    borderRadius: 0,
                    bgcolor: "#fff",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "12px",
                    py: "6px",
                    px: 0,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: 0,
                width: compactMobile ? "100%" : "auto",
                justifyContent: compactMobile ? "flex-end" : "flex-start",
              }}
            >
              <Tooltip title="Refresh">
                <IconButton
                  size="small"
                  sx={{ p: "4px", width: 28, height: 28, border: "1px solid #e5e7eb", borderRadius: "50%", color: "#6b7280" }}
                  onClick={activeTab === "Landmark" ? () => hydrateLandmarkLocation().catch(() => {}) : handleRefreshClick}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="List view">
                <IconButton size="small" sx={{ p: "4px", width: 28, height: 28, border: "1px solid #e5e7eb", borderRadius: "50%", color: "#6b7280" }} onClick={() => openQuickModal("devices")}><ViewListIcon sx={{ fontSize: 16 }} /></IconButton>
              </Tooltip>
              {activeTab === "Objects" ? (
                <Tooltip title="Add device">
                  <IconButton size="small" sx={{ p: "4px", width: 28, height: 28, border: "1px solid #f7cadc", borderRadius: "50%", color: "#e11d74" }} onClick={onAddDevice}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : activeTab === "Geofence" && onCreateSidebarGeofence ? (
                <Tooltip title="Add geofence">
                  <IconButton size="small" sx={{ p: "4px", width: 28, height: 28, border: "1px solid #f7cadc", borderRadius: "50%", color: "#e11d74" }} onClick={(event) => setGeofenceMenuAnchor(event.currentTarget)}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : activeTab === "Landmark" ? (
                <Tooltip title="Add landmark">
                  <IconButton size="small" sx={{ p: "4px", width: 28, height: 28, border: "1px solid #f7cadc", borderRadius: "50%", color: "#e11d74" }} onClick={() => openCreateLandmarkForm()}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title={sidebarOpen ? "Collapse" : "Expand"}>
                <IconButton size="small" sx={{ p: "4px" }} onClick={() => setSidebarOpen((s) => !s)}>
                  {isMobile ? <CloseIcon sx={{ fontSize: 16 }} /> : sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )
      )}

      {/* Column headers */}
      {activeTab === "Objects" ? ColHeader : null}
      {activeTab === "Notifications" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: compactDesktop ? "1fr 1.15fr 0.95fr 28px" : "1.1fr 1.3fr 1fr 32px",
            gap: 1,
            px: 1.5,
            py: 0.7,
            background: "#f8fafc",
            borderTop: "1px solid #eef2f7",
            borderBottom: "1px solid #eef2f7",
          }}
        >
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>Object</Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>Time</Typography>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>Event</Typography>
          <Box />
        </Box>
      ) : null}
    </Box>
  );

  // ── Body ───────────────────────────────────────────────────────────────────
  const Body = (
    <Box
      ref={scrollRef}
      sx={{
        flex: 1,
        overflowY: activeTab === "Objects" ? "hidden" : "auto",
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
      }}
    >
      {activeTab === "History" ? (
        <Box sx={{ p: 0.75, background: "#fff" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "72px minmax(0,1fr)", gap: 1, alignItems: "center" }}>
            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>Reg No</Typography>
            <Box ref={historyDeviceDropdownRef} sx={{ position: "relative" }}>
              <TextField
                fullWidth
                value={historyDeviceQuery}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setHistoryDeviceQuery(nextValue);
                  if (!nextValue.trim()) {
                    setHistoryDeviceId("");
                    setHistoryVisible(false);
                    setHistoryError("");
                  } else if (
                    selectedHistoryDevice &&
                    safeLower(nextValue).trim() !==
                      safeLower(getHistoryDeviceLabel(selectedHistoryDevice)).trim()
                  ) {
                    setHistoryDeviceId("");
                  }
                  setHistoryDeviceDropdownOpen(true);
                }}
                onFocus={() => setHistoryDeviceDropdownOpen(true)}
                placeholder="Search for an object"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: "#fff" },
                  "& .MuiInputBase-input": { fontSize: "12px", py: "8px" },
                }}
              />
              {historyDeviceDropdownOpen && historyDeviceOptions.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 2px)",
                    left: 0,
                    right: 0,
                    height: 208,
                    overflowY: "auto",
                    bgcolor: "#fff",
                    border: "1px solid #dbe3ef",
                    boxShadow: "0 14px 28px rgba(15,23,42,0.14)",
                    borderRadius: "0 0 14px 14px",
                    zIndex: 5,
                    pt: 0.4,
                    pb: 0.4,
                  }}
                >
                  {historyDeviceOptions.map((device) => (
                    <Box
                      key={device?._id}
                      onClick={() => {
                        setHistoryDeviceId(device?._id);
                        setHistoryDeviceQuery(getHistoryDeviceLabel(device));
                        setHistoryDeviceDropdownOpen(false);
                      }}
                      sx={{
                        mx: 0.5,
                        px: 1.6,
                        py: 1.1,
                        fontSize: "12px",
                        cursor: "pointer",
                        borderRadius: 1.5,
                        color: String(historyDeviceId) === String(device?._id) ? "#0f172a" : "#1f2937",
                        fontWeight: String(historyDeviceId) === String(device?._id) ? 700 : 500,
                        background: String(historyDeviceId) === String(device?._id) ? "#eef4ff" : "transparent",
                        border: "1px solid transparent",
                        borderColor: String(historyDeviceId) === String(device?._id) ? "#bfdbfe" : "transparent",
                        "&:hover": { background: "#f8fafc" },
                      }}
                    >
                      {getHistoryDeviceLabel(device)}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>Filter</Typography>
            <Box
              onClick={() => setHistoryFilter((prev) => prev === "Today" ? "Custom" : "Today")}
              sx={{
                minHeight: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                border: "1px solid #dbe3ef",
                borderRadius: 1.5,
                bgcolor: "#fff",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              <span>{historyFilter}</span>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#fb7185" }} />
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>From</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 48px 18px 48px 38px", gap: 0.5, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #dbe3ef", borderRadius: 1.5, overflow: "hidden", bgcolor: "#fff" }}>
                <Box sx={{ px: 1, py: 0.9, background: "#eef2f7", display: "grid", placeItems: "center" }}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: "#374151" }} />
                </Box>
                <TextField
                  type="date"
                  value={historyFromDate}
                  onChange={(e) => {
                    markHistoryFilterCustom();
                    setHistoryFromDate(e.target.value);
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: "12px", px: 1.25, py: 1 } }}
                />
              </Box>
              <TextField select value={historyFromHour} onChange={(e) => { markHistoryFilterCustom(); setHistoryFromHour(e.target.value); }} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((hour) => <MenuItem key={hour} value={hour}>{hour}</MenuItem>)}
              </TextField>
              <Typography sx={{ textAlign: "center", fontWeight: 700 }}>:</Typography>
              <TextField select value={historyFromMinute} onChange={(e) => { markHistoryFilterCustom(); setHistoryFromMinute(e.target.value); }} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["00", "15", "30", "45", "59"].map((minute) => <MenuItem key={minute} value={minute}>{minute}</MenuItem>)}
              </TextField>
              <TextField select value={historyFromMeridiem} onChange={(e) => { markHistoryFilterCustom(); setHistoryFromMeridiem(e.target.value); }} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["AM", "PM"].map((part) => <MenuItem key={part} value={part}>{part}</MenuItem>)}
              </TextField>
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>To</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 48px 18px 48px 38px", gap: 0.5, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", border: "1px solid #dbe3ef", borderRadius: 1.5, overflow: "hidden", bgcolor: "#fff" }}>
                <Box sx={{ px: 1, py: 0.9, background: "#eef2f7", display: "grid", placeItems: "center" }}>
                  <CalendarMonthIcon sx={{ fontSize: 16, color: "#374151" }} />
                </Box>
                <TextField
                  type="date"
                  value={historyToDate}
                  onChange={(e) => {
                    markHistoryFilterCustom();
                    setHistoryToDate(e.target.value);
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ flex: 1, "& .MuiInputBase-input": { fontSize: "12px", px: 1.25, py: 1 } }}
                />
              </Box>
              <TextField select value={historyToHour} onChange={(e) => { markHistoryFilterCustom(); setHistoryToHour(e.target.value); }} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((hour) => <MenuItem key={hour} value={hour}>{hour}</MenuItem>)}
              </TextField>
              <Typography sx={{ textAlign: "center", fontWeight: 700 }}>:</Typography>
              <TextField select value={historyToMinute} onChange={(e) => { markHistoryFilterCustom(); setHistoryToMinute(e.target.value); }} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["00", "15", "30", "45", "59"].map((minute) => <MenuItem key={minute} value={minute}>{minute}</MenuItem>)}
              </TextField>
              <TextField select value={historyToMeridiem} onChange={(e) => { markHistoryFilterCustom(); setHistoryToMeridiem(e.target.value); }} size="small" sx={{ "& .MuiInputBase-input": { fontSize: "12px", py: "8px", textAlign: "center" } }}>
                {["AM", "PM"].map((part) => <MenuItem key={part} value={part}>{part}</MenuItem>)}
              </TextField>
            </Box>

            <Typography sx={{ fontSize: "11px", color: "text.primary" }}>Stops</Typography>
            <Box
              sx={{
                minHeight: 38,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                border: "1px solid #dbe3ef",
                borderRadius: 1.5,
                bgcolor: "#fff",
                fontSize: "12px",
              }}
            >
              <span>All</span>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#fb7185" }} />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.35fr", gap: 1.2, mt: 1.25 }}>
            <Box onClick={fetchHistoryReport} sx={{ py: 1, textAlign: "center", borderRadius: 1.25, bgcolor: "#f8fafc", border: "1px solid #eef2f7", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>Show</Box>
            <Box
              onClick={() => {
                setHistoryVisible(false);
                setHistoryError("");
                setHistoryResults([]);
                lastAutoPlaybackKeyRef.current = "";
                onClosePlayback?.();
              }}
              sx={{ py: 1, textAlign: "center", borderRadius: 1.25, bgcolor: "#f8fafc", border: "1px solid #eef2f7", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}
            >
              Hide
            </Box>
            <Box onClick={(e) => setHistoryExportAnchor(e.currentTarget)} sx={{ py: 1, textAlign: "center", borderRadius: 1.25, bgcolor: "#f8fafc", border: "1px solid #eef2f7", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>Import/Export</Box>
          </Box>

          {historyError ? (
            <Box sx={{ mt: 1, px: 0.75, color: "#dc2626", fontSize: "11px", fontWeight: 600 }}>{historyError}</Box>
          ) : null}

          {historyLoading ? (
            <Box sx={{ p: 2, textAlign: "center", color: "text.secondary", fontSize: "12px" }}>Loading history...</Box>
          ) : null}

          {historyVisible && historyResults.length ? (
            <Box sx={{ mt: 1.2, borderTop: "1px solid #eef2f7", pt: 1 }}>
              {historyResults.map((item) => (
                <Box
                  key={item.id}
                  onClick={async () => {
                    if (!historyDeviceId || typeof onOpenPlayback !== "function") return;
                    try {
                      const token = localStorage.getItem("token");
                      const apiBase =
                        process.env.REACT_APP_BACKEND_URL ||
                        process.env.REACT_APP_API_URL ||
                        "https://qylmox.in/api";
                      const startTs = new Date(item.startTimeRaw).toISOString();
                      const endTs = new Date(item.endTimeRaw).toISOString();
                      const pbResp = await fetch(
                        `${apiBase}/history/${historyDeviceId}/playback?startDate=${encodeURIComponent(historyFromDate)}&endDate=${encodeURIComponent(historyToDate)}&startTs=${encodeURIComponent(startTs)}&endTs=${encodeURIComponent(endTs)}`,
                        {
                          headers: {
                            Authorization: token ? `Bearer ${token}` : "",
                            "x-auth-token": token || "",
                            "Content-Type": "application/json",
                          },
                        }
                      );
                      if (!pbResp.ok) return;
                      const pb = await pbResp.json();
                      const points = Array.isArray(pb?.points) ? pb.points : [];
                      if (!points.length) return;
                      onOpenPlayback({
                        id: `history_${historyDeviceId}_${item.id}`,
                        deviceId: historyDeviceId,
                        date: item?.startLabel || historyFromDate,
                        route: points,
                        distance: Number(pb?.stats?.totalDistanceRawKm ?? 0),
                        durationSeconds: Number(pb?.stats?.movingTime ?? 0) + Number(pb?.stats?.idleTime ?? 0),
                        startTime: item.startTimeRaw,
                        endTime: item.endTimeRaw,
                      });
                    } catch {
                      // noop
                    }
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "112px 44px 1fr 78px",
                    gap: 1,
                    alignItems: "start",
                    px: 0.9,
                    py: 1,
                    borderBottom: "1px solid #eef2f7",
                    background: "#fff",
                    cursor: typeof onOpenPlayback === "function" ? "pointer" : "default",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#1f2937" }}>{item.startLabel}</Typography>
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#1f9d55" }} />
                      <Typography sx={{ fontSize: "10.5px", color: "#1f2937", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {item.startAddress}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "grid", placeItems: "center", pt: 0.25 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: `2px solid ${item.isRide ? "#1d4fff" : "#ff2d20"}`,
                        color: item.isRide ? "#1d4fff" : "#ff2d20",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      {item.isRide ? <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 14 }} /> : <Typography sx={{ fontSize: "11px", fontWeight: 800 }}>P</Typography>}
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#1f2937" }}>{item.durationText}</Typography>
                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#e43c4f" }} />
                      <Typography sx={{ fontSize: "10.5px", color: "#1f2937", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {item.endAddress}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "right", pt: 0.2 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#1f2937" }}>{item.distanceText}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : activeTab === "Notifications" ? (
        <Box sx={{ background: "#fff" }}>
          {notificationsRefreshing ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                borderBottom: "1px solid #eef2f7",
                background: "#f8fbff",
              }}
            >
              <CircularProgress size={14} />
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#2563eb" }}>
                Refreshing notifications...
              </Typography>
            </Box>
          ) : null}
          {filteredNotificationRows.map((alarm) => {
              const isSelected = String(selectedNotificationId || "") === String(alarm?._id || alarm?.alarmId || "");
              const hasCoords = Boolean(getNotificationCoords(alarm));
              return (
                <Box
                  key={alarm?._id || alarm?.alarmId}
                  onClick={() => hasCoords && onSelectNotification?.(alarm)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: compactDesktop ? "1fr 1.15fr 0.95fr 28px" : "1.1fr 1.3fr 1fr 32px",
                    gap: 1,
                    px: 1.5,
                    py: 1.05,
                    alignItems: "center",
                    borderBottom: "1px solid #eef2f7",
                    background: isSelected ? "#f8fbff" : "#fff",
                    cursor: hasCoords ? "pointer" : "default",
                    "&:hover": {
                      background: hasCoords ? "#f8fafc" : "#fff",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {alarm?.deviceName || alarm?.imei || "Unknown Device"}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {formatNotificationTime(alarm?.triggeredAt || alarm?.timestamp)}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {prettyNotificationEvent(alarm?.alarmType || alarm?.eventType)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    <IconButton
                      size="small"
                      onClick={(e) => openNotificationMenu(e, alarm)}
                      sx={{ p: "2px" }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          {!filteredNotificationRows.length ? (
            <Box sx={{ p: 2, color: "text.secondary", fontSize: "12px" }}>
              No notifications found.
            </Box>
          ) : null}
        </Box>
      ) : activeTab === "Landmark" ? (
        <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
          {landmarkFormOpen ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "text.primary" }}>
                {landmarkMode === "edit" ? "Edit Landmark" : "Add Landmark"}
              </Typography>
              <TextField
                value={landmarkForm.name}
                onChange={(event) => setLandmarkForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Landmark name"
                size="small"
                fullWidth
              />
              <TextField
                value={landmarkLocationQuery}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setLandmarkLocationQuery(nextValue);
                  setLandmarkSearchOpen(Boolean(nextValue.trim()));
                }}
                onFocus={() => {
                  if (landmarkSearchResults.length) {
                    setLandmarkSearchOpen(true);
                  }
                }}
                placeholder="Search location"
                size="small"
                fullWidth
                disabled={landmarkLocationLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {landmarkSearchLoading || landmarkLocationLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <SearchIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {landmarkSearchOpen ? (
                <Box
                  ref={landmarkSearchDropdownRef}
                  sx={{
                    mt: -0.4,
                    border: "1px solid #dbe3ef",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: "#fff",
                    boxShadow: "0 12px 26px rgba(15,23,42,0.12)",
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {landmarkSearchResults.map((result, index) => {
                    const address = String(result?.address || "").trim();
                    const title = address.split(",")[0]?.trim() || address;
                    const subtitle = address.includes(",") ? address.slice(address.indexOf(",") + 1).trim() : "";
                    return (
                      <Box
                        key={`${address}-${index}`}
                        onClick={() => handleLandmarkSuggestionSelect(result)}
                        sx={{
                          px: 1.25,
                          py: 1,
                          borderBottom: index === landmarkSearchResults.length - 1 ? "none" : "1px solid #eef2f7",
                          cursor: "pointer",
                          "&:hover": { background: "#f8fafc" },
                        }}
                      >
                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.primary" }}>
                          {title}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
                          {subtitle || address}
                        </Typography>
                      </Box>
                    );
                  })}
                  {!landmarkSearchResults.length && !landmarkSearchLoading ? (
                    <Box sx={{ px: 1.25, py: 1, fontSize: "12px", color: "text.secondary" }}>
                      No location found.
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              <TextField
                select
                value={landmarkForm.category}
                onChange={(event) => setLandmarkForm((prev) => ({ ...prev, category: event.target.value }))}
                size="small"
                fullWidth
              >
                {LANDMARK_CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {formatLandmarkCategoryLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                value={landmarkForm.radius}
                onChange={(event) => {
                  landmarkRadiusSyncSourceRef.current = "user";
                  setLandmarkForm((prev) => ({
                    ...prev,
                    radius: Math.max(0, Number(event.target.value || 0)),
                  }));
                }}
                placeholder="Radius (meters)"
                size="small"
                fullWidth
                inputProps={{ min: 0, step: 10 }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  onLandmarkDraftChange?.(null);
                  setLandmarkPlacementActive(true);
                }}
                sx={{ fontWeight: 700 }}
              >
                Click On Map To Draw Circle
              </Button>
              <TextField
                value={landmarkForm.description}
                onChange={(event) => setLandmarkForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Address"
                multiline
                minRows={3}
                fullWidth
              />
              {landmarkFormError ? (
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#dc2626" }}>
                  {landmarkFormError}
                </Typography>
              ) : null}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={closeLandmarkForm}
                  disabled={landmarkSaving}
                  sx={{ fontWeight: 700 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleLandmarkSubmit}
                  disabled={landmarkSaving || landmarkLocationLoading}
                  sx={{ fontWeight: 700 }}
                >
                  {landmarkSaving ? "Saving..." : "Submit"}
                </Button>
              </Box>
            </Box>
          ) : null}

          {landmarkItems.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {landmarkItems.map((item) => {
                const isSelected = String(selectedLandmarkId || "") === item.id;
                return (
                  <Box
                    key={item.id}
                    onClick={() => onSelectLandmark?.(item.raw || item)}
                  sx={{
                      px: 1,
                      py: 0.95,
                      borderRadius: 1.5,
                      border: "1px solid #e5e7eb",
                      background: isSelected ? "rgba(37, 99, 235, 0.08)" : "#fff",
                      cursor: "pointer",
                      display: "grid",
                      gridTemplateColumns: "38px minmax(0, 1fr) 84px 34px",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "999px",
                        display: "grid",
                        placeItems: "center",
                        background: "#fff7ed",
                        border: "2px solid",
                        borderColor: isSelected ? "primary.main" : "#fb923c",
                      }}
                    >
                      <PlaceOutlinedIcon sx={{ fontSize: 18, color: "#ea580c" }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ mt: 0.2, fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {formatLandmarkCategoryLabel(item.category)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.primary" }}>
                        {item.radiusText}
                      </Typography>
                      <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
                        {item.assignedCount > 0 ? `${item.assignedCount} Device` : "Radius"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#64748b" }}
                        onClick={(event) => openLandmarkMenu(event, item.raw || item)}
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : !landmarkFormOpen ? (
            <Box sx={{ p: 2, color: "text.secondary", fontSize: "13px" }}>
              No landmark found.
            </Box>
          ) : null}
        </Box>
      ) : activeTab === "Geofence" ? (
        geofenceItems.length ? (
          <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {geofenceItems.map((item) => {
              const isSelected = String(selectedGeofenceId || "") === item.id;
              return (
                <Box
                  key={item.id}
                  onClick={() => onSelectGeofence?.(item.raw || item)}
                  sx={{
                    px: 1,
                    py: 0.95,
                    borderRadius: 1.5,
                    border: "1px solid #e5e7eb",
                    background: isSelected ? "rgba(37, 99, 235, 0.08)" : "#fff",
                    cursor: "pointer",
                    display: "grid",
                    gridTemplateColumns: "38px minmax(0, 1fr) 84px 86px",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": {
                      borderColor: "primary.main",
                      background: "rgba(37, 99, 235, 0.05)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: item.type === "circle" ? "999px" : "8px",
                      flexShrink: 0,
                      display: "grid",
                      placeItems: "center",
                      background: "#fff",
                      border: "2px solid",
                      borderColor: isSelected ? "primary.main" : "#9ca3af",
                    }}
                  />

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.name}
                    </Typography>
                    <Typography sx={{ mt: 0.2, fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "capitalize" }}>
                      {item.category || item.type}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "text.primary" }}>
                      {item.radiusText || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
                      {item.radiusText ? "Radius" : `${item.assignedCount || 0} Vehicle`}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
                    {onEditSidebarGeofence ? (
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#f43f5e" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditSidebarGeofence(item.raw || item);
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                    {onDeleteSidebarGeofence ? (
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#f43f5e" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteSidebarGeofence(item.raw || item);
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                    {onAssignSidebarGeofence ? (
                      <IconButton
                        size="small"
                        sx={{ p: "2px", color: "#2563eb" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onAssignSidebarGeofence(item.raw || item);
                        }}
                      >
                        <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ p: 2, color: "text.secondary", fontSize: "13px" }}>
            No geofence found.
          </Box>
        )
      ) : (
        objectRows.length ? (
          <Box sx={{ height: "100%" }}>
            <InfiniteLoader
              isItemLoaded={(index) => index < objectRows.length}
              itemCount={objectRows.length + (pagedObjectsHasMore ? 1 : 0)}
              loadMoreItems={async () => {
                if (!pagedObjectsHasMore || pagedObjectsLoading) return;
                await fetchObjectDevicesPage({ page: pagedObjectsPage + 1 });
              }}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  key={`${activeStatus}:${searchTerm}:${activeTab}`}
                  ref={ref}
                  height={objectsListHeight}
                  width="100%"
                  itemCount={objectRows.length + (pagedObjectsHasMore ? 1 : 0)}
                  itemSize={compactDesktop ? 68 : 80}
                  itemKey={(index) => objectRows[index]?.key || `row-${index}`}
                  overscanCount={10}
                  onItemsRendered={({ overscanStartIndex, overscanStopIndex, visibleStartIndex, visibleStopIndex }) =>
                    onItemsRendered({
                      overscanStartIndex,
                      overscanStopIndex,
                      visibleStartIndex,
                      visibleStopIndex,
                    })
                  }
                >
                  {renderObjectRow}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          </Box>
        ) : pagedObjectsLoading ? (
          <Box sx={{ p: 2, color: "text.secondary", fontSize: "13px", display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            Loading devices...
          </Box>
        ) : (
          <Box sx={{ p: 2, color: "text.secondary", fontSize: "13px" }}>
            No devices found.
          </Box>
        )
      )}
    </Box>
  );

  // ── Footer ─────────────────────────────────────────────────────────────────
  const Footer = (
    <Box sx={{ px: 1.5, py: 1, borderTop: "0.5px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: connected ? "success.main" : "error.main" }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "text.secondary" }}>
        {connected ? "Connected" : "Disconnected"}
      </Typography>
    </Box>
  );

  // ── Context Menu ───────────────────────────────────────────────────────────
  const MoreMenu = (
    <>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition || undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuPosition)}
        onClose={closeMoreMenu}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 240,
            borderRadius: 2,
            background: "#eef4fb",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.22)",
            overflow: "hidden",
            "& .MuiMenuItem-root": {
              py: 1.5,
              px: 2,
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#1f2937",
              gap: 1,
            },
            "& .MuiListItemIcon-root": {
              minWidth: 34,
              color: "#1f2937",
            },
          },
        }}
      >
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) onEditDevice?.(d); }}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) handleOpenCommandModal?.(d, { uiMode: "simple", commandScope: "full" }); }}>
          <ListItemIcon><BoltIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Send GPRS Command" />
        </MenuItem>
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) openShareDialog(d); }}>
          <ListItemIcon><ShareOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Share location" />
        </MenuItem>
        <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) openDeviceHistoryPanel(d); }}>
          <ListItemIcon><HistoryOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Show history" />
        </MenuItem>
        {onOpenGeofence && (
          <MenuItem onClick={() => { const d = menuDevice; closeMoreMenu(); if (d) onOpenGeofence?.(d); }}>
            <ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Geofence" />
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={historyExportAnchor}
        open={Boolean(historyExportAnchor)}
        onClose={closeHistoryExportMenu}
        PaperProps={{
          sx: {
            minWidth: 190,
            borderRadius: 2,
            "& .MuiMenuItem-root": {
              fontSize: "13px",
              fontWeight: 600,
              gap: 1,
            },
          },
        }}
      >
        <MenuItem onClick={() => exportHistoryData("csv")}>
          <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Export CSV" />
        </MenuItem>
        <MenuItem onClick={() => exportHistoryData("json")}>
          <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Export JSON" />
        </MenuItem>
        <MenuItem onClick={() => historyImportInputRef.current?.click()}>
          <ListItemIcon><FileUploadOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Import File" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={notificationEventFilterAnchor}
        open={Boolean(notificationEventFilterAnchor)}
        onClose={() => setNotificationEventFilterAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 240,
            maxHeight: 320,
            borderRadius: 2,
            overflowY: "auto",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.22)",
          },
        }}
      >
        {notificationItems.eventOptions.length ? (
          notificationItems.eventOptions.map((eventName) => {
            const checked = notificationEventFilters.includes(eventName);
            return (
              <MenuItem
                key={eventName}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggleNotificationEventFilter(eventName);
                }}
                sx={{ py: 1.1, px: 1.5 }}
              >
                <Checkbox checked={checked} size="small" />
                <ListItemText primary={prettyNotificationEvent(eventName)} />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>No event types available</MenuItem>
        )}
        {notificationItems.eventOptions.length ? (
          <MenuItem
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setNotificationEventFilters([]);
            }}
            sx={{ py: 1.1, px: 1.5, borderTop: "1px solid #eef2f7", fontWeight: 700 }}
          >
            Clear Filter
          </MenuItem>
        ) : null}
      </Menu>

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={notificationMenuPosition || undefined}
        anchorEl={notificationMenuAnchor}
        open={Boolean(notificationMenuPosition)}
        onClose={closeNotificationMenu}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 190,
            borderRadius: 2,
            background: "#eef4fb",
            boxShadow: "0 12px 30px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.22)",
            "& .MuiMenuItem-root": {
              py: 1.3,
              px: 2,
              fontSize: "0.92rem",
              fontWeight: 600,
              gap: 1,
            },
          },
        }}
      >
        <MenuItem onClick={openRemarkDialog}>
          <ListItemIcon><NoteAltOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Add Remark" />
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={geofenceMenuAnchor}
        open={Boolean(geofenceMenuAnchor)}
        onClose={() => setGeofenceMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setGeofenceMenuAnchor(null);
            onCreateSidebarGeofence?.("polygon");
          }}
        >
          Polygon geofence
        </MenuItem>
        <MenuItem
          onClick={() => {
            setGeofenceMenuAnchor(null);
            onCreateSidebarGeofence?.("circle");
          }}
        >
          Circular geofence
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={landmarkMenuAnchor}
        open={Boolean(landmarkMenuAnchor)}
        onClose={closeLandmarkMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 220,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const landmark = menuLandmark;
            closeLandmarkMenu();
            if (landmark) {
              openEditLandmarkForm(landmark);
            }
          }}
        >
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        {onDeleteSidebarLandmark ? (
          <MenuItem
            onClick={() => {
              const landmark = menuLandmark;
              closeLandmarkMenu();
              if (landmark) {
                onDeleteSidebarLandmark(landmark);
              }
            }}
          >
            <ListItemIcon><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Delete" />
          </MenuItem>
        ) : null}
        {onAssignSidebarLandmark ? (
          <MenuItem
            onClick={() => {
              const landmark = menuLandmark;
              closeLandmarkMenu();
              if (landmark) {
                onAssignSidebarLandmark(landmark);
              }
            }}
          >
            <ListItemIcon><DirectionsCarFilledOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Assign Device" />
          </MenuItem>
        ) : null}
      </Menu>

      <input
        ref={historyImportInputRef}
        type="file"
        accept=".json,.csv"
        style={{ display: "none" }}
        onChange={handleImportHistoryFile}
      />

      <Dialog open={shareDialogOpen} onClose={closeShareDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          Share Location
          <IconButton onClick={closeShareDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 14, color: "#4b5563", mb: 1 }}>
            {shareDevice?.name || shareDevice?.vehicleNumber || shareDevice?.imei || "Vehicle"}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
            <Box sx={{ minWidth: { xs: "100%", sm: 180 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75, color: "#111827" }}>
                Valid For
              </Typography>
              <Select
                fullWidth
                size="small"
                value={sharePreset}
                onChange={(event) => setSharePreset(event.target.value)}
              >
                {SHARE_PRESETS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {sharePreset === "custom" ? (
              <TextField
                label="Hours"
                type="number"
                size="small"
                value={shareCustomHours}
                onChange={(event) => setShareCustomHours(event.target.value)}
                inputProps={{ min: 1, max: 720 }}
                sx={{ width: { xs: "100%", sm: 130 } }}
              />
            ) : null}
            <Button
              variant="contained"
              onClick={handleRegenerateShareLink}
              disabled={shareLoading}
              sx={{ minWidth: 140, height: 40, alignSelf: { xs: "stretch", sm: "flex-end" } }}
            >
              {shareLoading ? "Generating..." : "Generate Link"}
            </Button>
          </Stack>

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e5e7eb" }}>
            {shareLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, color: "#475569" }}>
                <CircularProgress size={18} />
                <Typography sx={{ fontWeight: 600 }}>Creating public tracking link...</Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 0.75 }}>
                  Public Link
                </Typography>
                <Typography sx={{ fontSize: 14, color: shareLink ? "#111827" : "#9ca3af", wordBreak: "break-all" }}>
                  {shareLink || "Generate link to share live location."}
                </Typography>
                {shareExpiresAt ? (
                  <Typography sx={{ fontSize: 12, color: "#64748b", mt: 1 }}>
                    Valid till: {new Date(shareExpiresAt).toLocaleString()}
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<ContentCopyIcon />} onClick={handleCopyShareLink} disabled={!shareLink || shareLoading}>
              Copy
            </Button>
            <Button startIcon={<OpenInNewIcon />} onClick={handleOpenShareLink} disabled={!shareLink || shareLoading}>
              Open
            </Button>
            <Button startIcon={<WhatsAppIcon />} onClick={handleWhatsAppShare} disabled={!shareLink || shareLoading}>
              WhatsApp
            </Button>
          </Stack>
          <Button onClick={closeShareDialog} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={remarkDialogOpen}
        onClose={() => {
          setRemarkDialogOpen(false);
          setRemarkAlarm(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1rem" }}>
          Add Remark
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            placeholder="Write remark here"
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              setRemarkDialogOpen(false);
              setRemarkAlarm(null);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRemark}
            disabled={savingRemark}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <Box
      className="ds-container"
      sx={{
        width: { xs: "100%", md: DRAWER_WIDTH },
        minWidth: { xs: "100%", md: DRAWER_WIDTH },
        maxWidth: { xs: "100%", md: DRAWER_WIDTH },
        height: compactMobile ? `calc(100dvh - ${MOBILE_HEADER_OFFSET + 18}px)` : "100%",
        minHeight: 0,
        background: "#fff",
        borderRight: "1px solid #dfe5ee",
        overflow: "hidden",
        position: "relative",
        borderRadius: compactMobile ? "18px" : 0,
        boxShadow: compactMobile ? "0 16px 32px rgba(15,23,42,0.18)" : "0 10px 28px rgba(15,23,42,0.08)",
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {Header}
        {Body}
        {Footer}
      </Box>
      {MoreMenu}

    </Box>
  );
}
